import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  Clock1,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  LogIn,
  Package,
  PenLine,
  Radio,
  Search,
  Send,
  UserRound,
  UserPlus,
  X,
} from "lucide-react";
import LoginModal from "./LoginModal";
import SignupModal from "./SignupModal";
import SiteFooter from "./SiteFooter";
import logo from "../assets/logo.png";
import {
  checkSharedCodeAvailability,
  lookupTransmission,
  replyToTransmission,
  sendTransmission,
  type BlackboxAccessMode,
  type BlackboxCodeType,
  type TransmissionCategory,
  type TransmissionLookup,
} from "../api/blackbox";
import { formatBlackspaceFullDateTime } from "../utils/time";

type SectionId = "blackbox";
type PublicNotice = { kind: "ok" | "err"; title: string; message: string };

const categories: TransmissionCategory[] = ["Feedback", "Bug", "Report", "Idea", "Contact"];
const sharedExpiryOptions = [
  { value: 1, label: "1 hour", icon: Clock1 },
  { value: 24, label: "24 hours", icon: Clock },
  { value: 72, label: "3 days", icon: CalendarClock },
  { value: 168, label: "7 days", icon: CalendarDays },
  { value: 720, label: "30 days", icon: CalendarRange },
] as const;
const categoryTone: Record<TransmissionCategory, string> = {
  Feedback: "tone-cyan",
  Bug: "tone-rose",
  Report: "tone-amber",
  Idea: "tone-violet",
  Contact: "tone-emerald",
};
const PUBLIC_AUTH_NOTICE_KEY = "blackspace.publicAuthNotice";
const GENERATED_RECEIPT_PATTERN = /^BS-[A-F0-9]{8}$/;
const SIX_DIGIT_PASSWORD_PATTERN = /^\d{6}$/;
const MODAL_FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])";
type SharedCodeAvailability = "idle" | "checking" | "available" | "failed";

function keepTabInsideModal(event: KeyboardEvent, modal: HTMLElement | null) {
  if (event.key !== "Tab" || !modal) return;
  const controls = Array.from(modal.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE))
    .filter((element) => element.getClientRects().length > 0);
  if (!controls.length) {
    event.preventDefault();
    modal.focus();
    return;
  }

  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && (document.activeElement === modal || document.activeElement === first || !modal.contains(document.activeElement))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
    event.preventDefault();
    first.focus();
  }
}

type TrackingCredential = {
  code: string;
  password: string;
  generated: boolean;
  hasSeparator: boolean;
  separatorCount: number;
  validShared: boolean;
};

function parseTrackingCredential(value: string): TrackingCredential {
  const normalized = value.trim().toUpperCase();
  const separatorCount = (normalized.match(/\+/g) || []).length;
  const separatorIndex = normalized.indexOf("+");
  const hasSeparator = separatorIndex >= 0;
  const code = (hasSeparator ? normalized.slice(0, separatorIndex) : normalized).trim();
  const password = hasSeparator ? normalized.slice(separatorIndex + 1).trim() : "";

  return {
    code,
    password,
    generated: GENERATED_RECEIPT_PATTERN.test(code) && !hasSeparator,
    hasSeparator,
    separatorCount,
    validShared: Boolean(
      code
      && !GENERATED_RECEIPT_PATTERN.test(code)
      && separatorCount === 1
      && SIX_DIGIT_PASSWORD_PATTERN.test(password)
    ),
  };
}

function resizeMobileTransmissionField(textarea: HTMLTextAreaElement | null) {
  if (!textarea) return;
  if (!window.matchMedia("(max-width: 640px)").matches) {
    textarea.style.height = "";
    textarea.style.overflowY = "";
    return;
  }

  textarea.style.height = "auto";
  textarea.style.height = `${Math.max(textarea.scrollHeight, 44)}px`;
  textarea.style.overflowY = "hidden";
}

function validSharedCodeShape(value: string) {
  return value.length >= 10
    && value.length <= 48
    && /[A-Z]/.test(value)
    && /\d/.test(value)
    && /^[A-Z0-9-]+$/.test(value)
    && !GENERATED_RECEIPT_PATTERN.test(value);
}

function publicNoticeFromCode(code: string | null): PublicNotice | null {
  if (code === "account-deleted") {
    return {
      kind: "ok",
      title: "Account deleted",
      message: "Your Blackspace account was deleted successfully.",
    };
  }
  return null;
}

export default function Homepage() {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [loginMode, setLoginMode] = useState<"login" | "recovery">("login");
  const [openSection, setOpenSection] = useState<SectionId | null>(null);
  const [publicNotice, setPublicNotice] = useState<PublicNotice | null>(null);
  const [headerTooltip, setHeaderTooltip] = useState<"login" | "signup" | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const browseAnonymously = () => {
    navigate("/home");
  };

  const openLogin = (mode: "login" | "recovery" = "login") => {
    setLoginMode(mode);
    setShowLogin(true);
    setShowSignup(false);
  };

  const openSignup = () => {
    setShowSignup(true);
    setShowLogin(false);
  };

  useEffect(() => {
    const mode = new URLSearchParams(window.location.search).get("mode");
    if (mode === "login") openLogin("login");
    if (mode === "recovery") openLogin("recovery");
    if (mode === "signup") openSignup();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current instanceof HTMLElement && !modalRef.current.contains(event.target as Node)) {
        setShowLogin(false);
        setShowSignup(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowLogin(false);
        setShowSignup(false);
      }
    };
    const listening = showLogin || showSignup;
    if (listening) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showLogin, showSignup]);

  useEffect(() => {
    if (!showLogin && !showSignup) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      const firstInput = modalRef.current?.querySelector<HTMLElement>("input:not([disabled])");
      (firstInput || modalRef.current)?.focus();
    });
    const trapTab = (event: KeyboardEvent) => keepTabInsideModal(event, modalRef.current);
    document.addEventListener("keydown", trapTab);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", trapTab);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [showLogin, showSignup]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const queryNotice = publicNoticeFromCode(url.searchParams.get("notice"));
    if (queryNotice) {
      setPublicNotice(queryNotice);
      url.searchParams.delete("notice");
      const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", cleanUrl || "/login");
      sessionStorage.removeItem(PUBLIC_AUTH_NOTICE_KEY);
      return;
    }

    const raw = sessionStorage.getItem(PUBLIC_AUTH_NOTICE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PUBLIC_AUTH_NOTICE_KEY);
    try {
      const parsed = JSON.parse(raw) as PublicNotice;
      if (parsed?.title && parsed?.message) setPublicNotice(parsed);
    } catch {
      setPublicNotice({
        kind: "ok",
        title: "Done",
        message: "Your request was completed successfully.",
      });
    }
  }, []);

  useEffect(() => {
    if (!publicNotice) return;
    const timer = window.setTimeout(() => setPublicNotice(null), 6500);
    return () => window.clearTimeout(timer);
  }, [publicNotice]);

  return (
    <div className="blackspace-public relative flex min-h-[100dvh] flex-col overflow-hidden bg-black text-white">
      <PublicPageStyles />

      {publicNotice && (
        <div className="fixed left-1/2 top-4 z-[80] w-[min(calc(100vw-1.5rem),28rem)] -translate-x-1/2">
          <div className="relative overflow-hidden rounded-lg border border-emerald-300/20 bg-black/82 p-4 shadow-2xl shadow-emerald-950/30 backdrop-blur-2xl">
            <div className="pointer-events-none absolute -left-16 -top-16 h-36 w-36 rounded-full bg-emerald-400/14 blur-3xl" />
            <div className="relative flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-emerald-400/10 text-emerald-200">
                <CheckCircle2 size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-black text-white">{publicNotice.title}</div>
                <p className="mt-1 text-xs font-semibold leading-5 text-neutral-400">{publicNotice.message}</p>
              </div>
              <button
                type="button"
                onClick={() => setPublicNotice(null)}
                className="grid h-8 w-8 shrink-0 place-items-center text-neutral-500 transition hover:text-white"
                aria-label="Dismiss notice"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="bs-public-header absolute inset-x-0 top-0 z-20 px-4 pt-3 sm:px-6 sm:pt-5">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
          <Link to="/login" className="bs-public-brand-link flex min-h-11 min-w-0 items-center gap-3 rounded-md text-left" aria-label="Blackspace home">
            <span className="bs-logo-signal bs-public-logo-frame inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md sm:h-11 sm:w-11" aria-hidden="true">
              <img src={logo} alt="" className="bs-public-logo-image h-full w-full object-cover" />
            </span>
            <span className="min-w-0">
              <span className="bs-public-brand-title block text-xl font-black leading-none text-white sm:text-2xl">Blackspace</span>
              <span className="bs-public-brand-subtitle mt-1 hidden text-xs font-semibold text-cyan-200/80 sm:block">Social realm for signal</span>
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <button
              onClick={() => openLogin("login")}
              onMouseEnter={() => setHeaderTooltip("login")}
              onMouseLeave={() => setHeaderTooltip(null)}
              onFocus={() => setHeaderTooltip("login")}
              onBlur={() => setHeaderTooltip(null)}
              aria-label="Sign in"
              title="Sign in"
              className="bs-public-login-button inline-flex h-9 items-center gap-2 rounded-md border bg-black/35 px-3 text-sm font-semibold backdrop-blur transition sm:h-10"
            >
              <LogIn size={16} />
              <span className={`bs-public-header-tooltip ${headerTooltip === "login" ? "is-visible" : ""}`} aria-hidden="true">Sign in</span>
            </button>
            <button
              onClick={openSignup}
              onMouseEnter={() => setHeaderTooltip("signup")}
              onMouseLeave={() => setHeaderTooltip(null)}
              onFocus={() => setHeaderTooltip("signup")}
              onBlur={() => setHeaderTooltip(null)}
              aria-label="Create identity"
              title="Create identity"
              className="bs-public-signup-button inline-flex h-9 items-center gap-2 rounded-md bg-white px-3 text-sm font-black text-black transition hover:bg-cyan-100 sm:h-10"
            >
              <UserPlus size={16} />
              <span className={`bs-public-header-tooltip ${headerTooltip === "signup" ? "is-visible" : ""}`} aria-hidden="true">Create identity</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex flex-1">
        <section className="bs-public-hero-section relative flex flex-1 items-center overflow-hidden px-4 pb-[5.5rem] pt-[5.25rem] sm:px-6 sm:pb-[5.25rem] sm:pt-[6.2rem]">
          <AnimatedSocialStage />
          <div className="bs-public-side-note" aria-hidden="true">
            <span>A quieter internet</span>
          </div>
          <div className="bs-public-hero-grid relative z-10 mx-auto grid w-full max-w-7xl items-center gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(22rem,0.72fr)] lg:gap-10">
            <div className="bs-public-copy max-w-3xl">
              <div className="bs-public-status-badge mb-3 inline-flex items-center gap-2 rounded-md border border-cyan-300/25 bg-cyan-300/10 px-3 py-1.5 text-[11px] font-bold text-cyan-100 sm:mb-5 sm:py-2 sm:text-xs">
                <span className="bs-public-ripple" aria-hidden="true"><span /><i /><b /></span>
                <span className="bs-public-awake-signal" aria-hidden="true">
                  <Radio size={15} />
                </span>
                <span className="bs-public-status-text">The room is awake</span>
              </div>

              <div className="bs-public-headline">
                <h1 className="bs-public-hero-title max-w-4xl text-[clamp(2.65rem,11vw,6.2rem)] font-black leading-[0.88] text-white sm:leading-[0.9]">
                  The room is <span>awake</span>
                </h1>

                <p className="bs-public-hero-copy mt-4 max-w-2xl text-sm leading-6 text-neutral-300 sm:mt-5 sm:text-base sm:leading-7">
                  <span className="bs-public-copy-desktop">A private space for real conversations,<br />shared only when you choose.</span>
                  <span className="bs-public-copy-mobile">A private space for real conversations, shared only when you choose.</span>
                </p>
              </div>

              <div className="bs-public-actions mt-5 flex flex-wrap gap-2.5 sm:mt-7 sm:gap-3">
                <button
                  onClick={browseAnonymously}
                  className="bs-public-action-card inline-flex h-11 items-center gap-2 rounded-md border border-cyan-300/30 bg-cyan-300/10 px-4 text-sm font-bold text-cyan-100 transition hover:bg-cyan-300/15 sm:h-12 sm:px-5"
                >
                  <span className="bs-public-action-icon"><Search size={17} /></span>
                  <span className="bs-public-action-text">
                    <strong>Anonymous</strong>
                    <small>Browse without an account</small>
                  </span>
                  <ArrowRight className="bs-public-action-arrow" size={17} />
                </button>
                <button
                  onClick={() => setOpenSection("blackbox")}
                  className="bs-public-action-card bs-public-blackbox-card inline-flex h-11 items-center gap-2 rounded-md border border-fuchsia-300/35 bg-fuchsia-400/10 px-4 text-sm font-bold text-fuchsia-100 transition hover:bg-fuchsia-400/15 sm:h-12 sm:px-5"
                >
                  <span className="bs-public-action-icon"><Package size={17} /></span>
                  <span className="bs-public-action-text">
                    <strong>Blackbox</strong>
                    <small>Private by design</small>
                  </span>
                  <ArrowRight className="bs-public-action-arrow" size={17} />
                </button>
              </div>
            </div>

          </div>
        </section>

      </main>

      <SiteFooter />

      {showLogin && (
        <div className="blackspace-auth-shell fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/76 p-4 backdrop-blur-sm">
          <div ref={modalRef} className="w-full max-w-md" role="dialog" aria-modal="true" aria-label="Sign in" tabIndex={-1}>
            <LoginModal initialMode={loginMode} onClose={() => setShowLogin(false)} onSwitchToSignup={openSignup} />
          </div>
        </div>
      )}
      {showSignup && (
        <div className="blackspace-auth-shell fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/76 p-4 backdrop-blur-sm">
          <div ref={modalRef} className="w-full max-w-md" role="dialog" aria-modal="true" aria-label="Create identity" tabIndex={-1}>
            <SignupModal onClose={() => setShowSignup(false)} onSwitchToLogin={() => openLogin("login")} />
          </div>
        </div>
      )}

      <BlackboxDialog open={openSection === "blackbox"} onClose={() => setOpenSection(null)} includeStyles={false} />
    </div>
  );
}

function AnimatedSocialStage() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="bs-public-horizon" />
    </div>
  );
}

export function BlackboxDialog({ open, onClose, initialCategory = "Feedback", includeStyles = true }: { open: boolean; onClose: () => void; initialCategory?: TransmissionCategory; includeStyles?: boolean }) {
  return (
    <>
      {open && includeStyles && <PublicPageStyles />}
      <SectionModal open={open} title="The Blackbox" onClose={onClose}>
        <TransmissionForm initialCategory={initialCategory} />
      </SectionModal>
    </>
  );
}

function SectionModal({ open, title, children, onClose }: { open: boolean; title: string; children: React.ReactNode; onClose: () => void }) {
  const isBlackbox = title === "The Blackbox";
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => panelRef.current?.focus());
    const handleKeys = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
      keepTabInsideModal(event, panelRef.current);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeys);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeys);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="bs-section-modal-backdrop fixed inset-0 z-[2300] flex items-center justify-center bg-black/78 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`bs-section-modal-panel ${isBlackbox ? "bs-blackbox-modal" : ""} w-full max-w-3xl overflow-hidden rounded-lg border border-white/10 bg-neutral-950 shadow-2xl shadow-black/70`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bs-section-modal-title"
        tabIndex={-1}
      >
        {isBlackbox && <div className="bs-blackbox-signal-line" aria-hidden="true" />}
        <div className="bs-section-modal-header flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="bs-section-modal-heading">
            {isBlackbox && (
              <span className="bs-blackbox-mark" aria-hidden="true">
                <Package size={18} />
              </span>
            )}
            <div>
              <h2 id="bs-section-modal-title" className="text-xl font-black text-white">{title}</h2>
              {isBlackbox && <p>Send a signal without attaching your public identity.</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="bs-section-modal-close inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/10 text-neutral-400 transition hover:bg-white/10 hover:text-white"
            aria-label={`Close ${title}`}
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="bs-section-modal-body max-h-[78svh] overflow-y-auto p-5 text-sm leading-6 text-neutral-300">{children}</div>
      </div>
    </div>
  );
}

function TransmissionForm({ initialCategory = "Feedback" }: { initialCategory?: TransmissionCategory }) {
  const [category, setCategory] = useState<TransmissionCategory>(initialCategory);
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [codeType, setCodeType] = useState<BlackboxCodeType>("generated");
  const [customCode, setCustomCode] = useState("");
  const [senderPassword, setSenderPassword] = useState("");
  const [receiverPassword, setReceiverPassword] = useState("");
  const [showSenderPassword, setShowSenderPassword] = useState(false);
  const [showReceiverPassword, setShowReceiverPassword] = useState(false);
  const [codeAvailability, setCodeAvailability] = useState<SharedCodeAvailability>("idle");
  const [accessMode, setAccessMode] = useState<BlackboxAccessMode>("read_write");
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [receipt, setReceipt] = useState("");
  const [receiptType, setReceiptType] = useState<BlackboxCodeType>("generated");
  const [receiptExpiresAt, setReceiptExpiresAt] = useState<string | null>(null);
  const [receiptCopied, setReceiptCopied] = useState(false);
  const [receiptCopyError, setReceiptCopyError] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const messageFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const charsLeft = useMemo(() => 3000 - message.length, [message]);
  const messageReady = message.trim().length >= 9;
  const selectedExpiryLabel = sharedExpiryOptions.find((option) => option.value === expiresInHours)?.label || "24 hours";

  useEffect(() => {
    resizeMobileTransmissionField(messageFieldRef.current);
  }, [message]);

  useEffect(() => {
    const syncHeight = () => resizeMobileTransmissionField(messageFieldRef.current);
    window.addEventListener("resize", syncHeight);
    return () => window.removeEventListener("resize", syncHeight);
  }, []);

  useEffect(() => {
    if (!error) return;
    const dismissTimer = window.setTimeout(() => setError(""), 4000);
    return () => window.clearTimeout(dismissTimer);
  }, [error]);

  useEffect(() => {
    if (!receipt) return;
    const dismissTimer = window.setTimeout(() => {
      setReceipt("");
      setReceiptExpiresAt(null);
      setReceiptCopied(false);
      setReceiptCopyError("");
    }, receiptType === "generated" ? (receiptCopied ? 1800 : 18000) : 6000);
    return () => window.clearTimeout(dismissTimer);
  }, [receipt, receiptCopied, receiptType]);

  useEffect(() => {
    if (codeType !== "custom") {
      setCodeAvailability("idle");
      return;
    }

    const cleanCode = customCode.trim().toUpperCase();
    if (!validSharedCodeShape(cleanCode)) {
      setCodeAvailability("idle");
      return;
    }

    const controller = new AbortController();
    setCodeAvailability("checking");
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkSharedCodeAvailability(cleanCode, controller.signal);
        setCodeAvailability(result.available ? "available" : "failed");
      } catch (availabilityError: any) {
        if (availabilityError?.name === "AbortError") return;
        setCodeAvailability("failed");
      }
    }, 420);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [codeType, customCode]);

  const submit = async () => {
    setError("");
    setReceipt("");
    setReceiptCopied(false);
    setReceiptCopyError("");
    if (!messageReady) {
      setError("Give the Blackbox at least 9 characters.");
      return;
    }
    const cleanCustomCode = customCode.trim().toUpperCase();
    if (codeType === "custom" && !validSharedCodeShape(cleanCustomCode)) {
      setError("Shared codes need at least 10 characters, including a letter and number.");
      return;
    }
    if (codeType === "custom" && codeAvailability !== "available") {
      setError(codeAvailability === "checking" ? "Wait for the code check to finish." : "Code failed.");
      return;
    }
    if (codeType === "custom" && (!SIX_DIGIT_PASSWORD_PATTERN.test(senderPassword) || !SIX_DIGIT_PASSWORD_PATTERN.test(receiverPassword))) {
      setError("Sender and receiver passwords must each contain exactly 6 digits.");
      return;
    }
    if (codeType === "custom" && senderPassword === receiverPassword) {
      setError("Sender and receiver passwords must be different.");
      return;
    }
    setSending(true);
    try {
      const result = await sendTransmission({
        category,
        message,
        contact,
        codeType,
        ...(codeType === "custom" ? {
          customCode: cleanCustomCode,
          senderPassword,
          receiverPassword,
          accessMode,
          expiresInHours,
        } : {}),
      });
      setReceipt(result.receiptCode);
      setReceiptType(result.codeType);
      setReceiptExpiresAt(result.expiresAt || null);
      setMessage("");
      setContact("");
      if (result.codeType === "custom") {
        setCodeType("generated");
        setCustomCode("");
        setSenderPassword("");
        setReceiverPassword("");
        setShowSenderPassword(false);
        setShowReceiverPassword(false);
        setCodeAvailability("idle");
      }
    } catch (err: any) {
      setError(err?.message || "Transmission failed.");
    } finally {
      setSending(false);
    }
  };

  const copyGeneratedReceipt = async () => {
    if (!receipt || receiptType !== "generated" || receiptCopied) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(receipt);
      setReceiptCopyError("");
      setReceiptCopied(true);
    } catch {
      setReceiptCopyError("Copy failed. Select the code to copy it manually.");
    }
  };

  return (
    <div className="bs-blackbox-workspace grid gap-5 lg:grid-cols-[1.08fr_0.92fr]">
      <div className="bs-blackbox-composer space-y-4">
        <div className="bs-blackbox-field-group">
          <span className="bs-blackbox-field-label">Transmission type</span>
          <div className="bs-blackbox-categories grid grid-cols-2 gap-2 sm:grid-cols-5" role="group" aria-label="Transmission type">
            {categories.map((item) => (
              <button
                key={item}
                onClick={() => {
                  setCategory(item);
                  if (error) setError("");
                }}
                className={`bs-blackbox-category ${categoryTone[item]} flex h-10 min-w-0 items-center justify-center rounded-md border px-3 text-center text-xs font-black transition ${category === item ? "is-active" : ""}`}
                aria-pressed={category === item}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <label className="bs-blackbox-field-group">
          <span className="bs-blackbox-field-label">Your transmission</span>
          <textarea
            ref={messageFieldRef}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value.slice(0, 3000));
              if (error) setError("");
            }}
            placeholder="Write what Blackspace should hear..."
            rows={1}
            className={`bs-blackbox-control bs-blackbox-message ${categoryTone[category]} w-full resize-none rounded-md border border-white/10 bg-black px-3 py-3 text-sm text-white outline-none transition`}
          />
        </label>

        <label className="bs-blackbox-field-group">
          <span className="bs-blackbox-field-label">Contact route <em>Optional</em></span>
          <input
            value={contact}
            onChange={(event) => {
              setContact(event.target.value.slice(0, 160));
              if (error) setError("");
            }}
            name="blackbox-contact-route"
            autoComplete="off"
            spellCheck={false}
            data-1p-ignore="true"
            data-bwignore="true"
            data-lpignore="true"
            className="bs-blackbox-control h-10 w-full rounded-md border border-white/10 bg-black px-3 text-sm text-white outline-none transition"
          />
        </label>

        <div className="bs-blackbox-submit-row flex flex-wrap items-center justify-between gap-3">
          <span className={`bs-blackbox-character-count ${charsLeft < 120 ? "is-low" : ""}`}>{charsLeft} characters left</span>
          <button
            type="button"
            onClick={submit}
            disabled={sending || !messageReady || (codeType === "custom" && codeAvailability !== "available")}
            className="bs-blackbox-submit"
          >
            <span className="bs-blackbox-submit-signal" aria-hidden="true"><Radio size={16} strokeWidth={1.8} /></span>
            <span className="bs-blackbox-submit-label">{sending ? "Transmitting..." : "Transmit securely"}</span>
          </button>
        </div>

        {error && <p className="bs-blackbox-alert is-error rounded-md border px-3 py-2 text-xs" role="alert">{error}</p>}
        {receipt && (
          <div className="bs-blackbox-alert is-success rounded-md border px-3 py-3 text-sm" role="status">
            <div className="flex items-center gap-2 font-black">
              <CheckCircle2 size={16} /> Transmission received.
            </div>
            <div className="bs-blackbox-receipt-code-row">
              <p>{receiptType === "custom" ? "Shared code" : "Receipt code"}: <code>{receipt}</code></p>
              {receiptType === "generated" && (
                <button
                  type="button"
                  className="bs-blackbox-copy-button"
                  onClick={() => void copyGeneratedReceipt()}
                  disabled={receiptCopied}
                  aria-label={receiptCopied ? "Receipt code copied" : "Copy receipt code"}
                  title={receiptCopied ? "Receipt code copied" : "Copy receipt code"}
                >
                  {receiptCopied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                  {receiptCopied ? "Copied" : "Copy"}
                </button>
              )}
            </div>
            {receiptCopyError && <p className="bs-blackbox-copy-error" role="alert">{receiptCopyError}</p>}
            {receiptType === "custom" && receiptExpiresAt && <p className="mt-1 text-xs">Available until {formatReceiptDate(receiptExpiresAt)}.</p>}
            {receiptType === "custom" && <p className="mt-1 text-xs">Sender and receiver must use their own 6-digit password to open it.</p>}
          </div>
        )}
      </div>

      <div className="bs-blackbox-receipt-panel rounded-md border border-white/10 bg-black/45 p-4">
        <div className="bs-blackbox-access-setup">
          <div className="bs-blackbox-field-group">
            <span className="bs-blackbox-field-label">Access code</span>
            <div className="bs-blackbox-code-mode" role="group" aria-label="Access code type">
              <button
                type="button"
                className={codeType === "generated" ? "is-active" : ""}
                aria-pressed={codeType === "generated"}
                onClick={() => {
                  setCodeType("generated");
                  if (error) setError("");
                }}
              >
                Generated receipt
              </button>
              <button
                type="button"
                className={codeType === "custom" ? "is-active" : ""}
                aria-pressed={codeType === "custom"}
                onClick={() => {
                  setCodeType("custom");
                  if (error) setError("");
                }}
              >
                Shared code
              </button>
            </div>
          </div>

          {codeType === "custom" && (
            <div className="bs-blackbox-shared-settings">
              <label className="bs-blackbox-field-group">
                <span className="bs-blackbox-field-label">Your shared code</span>
                <input
                  value={customCode}
                  onChange={(event) => {
                    setCustomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 48));
                    if (error) setError("");
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="bs-blackbox-control h-10 w-full rounded-md border border-white/10 bg-black px-3 font-mono text-sm text-white outline-none transition"
                />
                {validSharedCodeShape(customCode.trim().toUpperCase()) && codeAvailability !== "idle" && (
                  <span
                    className={`bs-blackbox-code-state is-${codeAvailability}`}
                    role="status"
                    aria-live="polite"
                  >
                    {codeAvailability === "checking" && <><Clock size={12} /> Checking…</>}
                    {codeAvailability === "available" && <><CheckCircle2 size={12} /> Code available</>}
                    {codeAvailability === "failed" && <><X size={12} /> Code failed.</>}
                  </span>
                )}
              </label>

              <div className="bs-blackbox-password-pairs">
                <div className="bs-blackbox-password-row is-sender">
                  <div className="bs-blackbox-password-title">
                    <UserRound size={14} aria-hidden="true" />
                    <span>Sender password</span>
                  </div>
                  <div className="bs-blackbox-password-input-shell">
                    <input
                      value={senderPassword}
                      onChange={(event) => {
                        setSenderPassword(event.target.value.replace(/\D/g, "").slice(0, 6));
                        if (error) setError("");
                      }}
                      type={showSenderPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      name="blackbox-sender-pin"
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-lpignore="true"
                      aria-label="Sender 6-digit password"
                      className="bs-blackbox-control h-10 min-w-0 w-full px-3 pr-10 font-mono text-sm tracking-[0.25em]"
                    />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setShowSenderPassword((visible) => !visible)}
                      aria-label={showSenderPassword ? "Hide sender password" : "Show sender password"}
                      aria-pressed={showSenderPassword}
                      title={showSenderPassword ? "Hide sender password" : "Show sender password"}
                    >
                      {showSenderPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="bs-blackbox-password-row is-receiver">
                  <div className="bs-blackbox-password-title">
                    <KeyRound size={14} aria-hidden="true" />
                    <span>Receiver password</span>
                  </div>
                  <div className="bs-blackbox-password-input-shell">
                    <input
                      value={receiverPassword}
                      onChange={(event) => {
                        setReceiverPassword(event.target.value.replace(/\D/g, "").slice(0, 6));
                        if (error) setError("");
                      }}
                      type={showReceiverPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      name="blackbox-receiver-pin"
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-lpignore="true"
                      aria-label="Receiver 6-digit password"
                      className="bs-blackbox-control h-10 min-w-0 w-full px-3 pr-10 font-mono text-sm tracking-[0.25em]"
                    />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setShowReceiverPassword((visible) => !visible)}
                      aria-label={showReceiverPassword ? "Hide receiver password" : "Show receiver password"}
                      aria-pressed={showReceiverPassword}
                      title={showReceiverPassword ? "Hide receiver password" : "Show receiver password"}
                    >
                      {showReceiverPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="bs-blackbox-shared-settings-grid">
                <div className="bs-blackbox-compact-setting">
                  <div className="bs-blackbox-compact-copy">
                    <span className="bs-blackbox-field-label">Receiver access</span>
                    <span className="bs-blackbox-selection-hint" aria-live="polite">
                      {accessMode === "read_only" ? "Read only" : "Read & write"}
                    </span>
                  </div>
                  <div className="bs-blackbox-icon-options is-access" role="group" aria-label="Receiver access">
                    <button
                      type="button"
                      className={accessMode === "read_only" ? "is-active" : ""}
                      aria-pressed={accessMode === "read_only"}
                      aria-label="Read only"
                      title="Read only"
                      onClick={() => {
                        setAccessMode("read_only");
                        if (error) setError("");
                      }}
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      type="button"
                      className={accessMode === "read_write" ? "is-active" : ""}
                      aria-pressed={accessMode === "read_write"}
                      aria-label="Read and write"
                      title="Read and write"
                      onClick={() => {
                        setAccessMode("read_write");
                        if (error) setError("");
                      }}
                    >
                      <PenLine size={15} />
                    </button>
                  </div>
                </div>

                <div className="bs-blackbox-compact-setting">
                  <div className="bs-blackbox-compact-copy">
                    <span className="bs-blackbox-field-label">Code expires</span>
                    <span className="bs-blackbox-selection-hint" aria-live="polite">{selectedExpiryLabel}</span>
                  </div>
                  <div
                    className="bs-blackbox-icon-options is-expiry"
                    role="group"
                    aria-label="Shared code expiry"
                  >
                    {sharedExpiryOptions.map((option) => {
                      const ExpiryIcon = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={expiresInHours === option.value ? "is-active" : ""}
                          aria-pressed={expiresInHours === option.value}
                          aria-label={option.label}
                          title={option.label}
                          onClick={() => {
                            setExpiresInHours(option.value);
                            if (error) setError("");
                          }}
                        >
                          <ExpiryIcon size={14} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <ReceiptLookup />
      </div>
    </div>
  );
}

function ReceiptLookup() {
  const [credential, setCredential] = useState("");
  const [activeCredential, setActiveCredential] = useState<{ code: string; password: string } | null>(null);
  const [lookup, setLookup] = useState<TransmissionLookup | null>(null);
  const [reply, setReply] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [replying, setReplying] = useState(false);
  const chatStreamRef = useRef<HTMLDivElement | null>(null);
  const lookupRowRef = useRef<HTMLDivElement | null>(null);
  const lookupResultRef = useRef<HTMLDivElement | null>(null);
  const lookupSessionRef = useRef(0);
  const parsedCredential = useMemo(() => parseTrackingCredential(credential), [credential]);
  const openedReceiptCode = lookup?.receiptCode;

  const trackingGuidance = useMemo(() => {
    if (lookup) return "Transmission opened";
    if (!credential.trim()) return "Generated receipt, or CODE+PASSWORD for shared access";
    if (parsedCredential.generated) return "Generated receipt ready";
    if (parsedCredential.separatorCount > 1) return "Use one + separator";
    if (!parsedCredential.hasSeparator) return "Use CODE+PASSWORD for shared access";
    if (!SIX_DIGIT_PASSWORD_PATTERN.test(parsedCredential.password)) {
      if (!/^\d*$/.test(parsedCredential.password) || parsedCredential.password.length > 6) {
        return "Use a 6-digit password";
      }
      return `Password ${Math.min(parsedCredential.password.length, 6)}/6`;
    }
    return "Shared transmission credential ready";
  }, [credential, lookup, parsedCredential]);

  useEffect(() => {
    if (!error) return;
    const dismissTimer = window.setTimeout(() => setError(""), 4000);
    return () => window.clearTimeout(dismissTimer);
  }, [error]);

  useEffect(() => {
    localStorage.removeItem("blackspace_last_receipt");
  }, []);

  useEffect(() => {
    if (!credential || lookup) return;
    const clearDraftOutsideTracking = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && lookupRowRef.current?.contains(target)) return;
      lookupSessionRef.current += 1;
      setCredential("");
    };
    document.addEventListener("click", clearDraftOutsideTracking, true);
    return () => document.removeEventListener("click", clearDraftOutsideTracking, true);
  }, [credential, lookup]);

  useEffect(() => {
    const stream = chatStreamRef.current;
    if (!stream || !lookup) return;
    stream.scrollTop = stream.scrollHeight;
  }, [lookup]);

  useEffect(() => {
    if (!openedReceiptCode) return;
    const frame = window.requestAnimationFrame(() => {
      lookupResultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [openedReceiptCode]);

  useEffect(() => {
    if (!openedReceiptCode) return;
    const chat = lookupResultRef.current;
    if (!chat) return;

    let idleTimer = 0;
    const closeOpenedTransmission = () => {
      lookupSessionRef.current += 1;
      setLookup(null);
      setActiveCredential(null);
      setCredential("");
      setReply("");
      setNotice("");
    };
    const resetIdleTimer = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(closeOpenedTransmission, 30_000);
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || !chat.contains(target)) {
        closeOpenedTransmission();
        return;
      }
      resetIdleTimer();
    };
    const handleInsideActivity = () => resetIdleTimer();

    resetIdleTimer();
    document.addEventListener("pointerdown", handlePointerDown, true);
    chat.addEventListener("focusin", handleInsideActivity, true);
    chat.addEventListener("input", handleInsideActivity, true);
    chat.addEventListener("keydown", handleInsideActivity, true);
    chat.addEventListener("scroll", handleInsideActivity, true);
    chat.addEventListener("touchmove", handleInsideActivity, true);

    return () => {
      window.clearTimeout(idleTimer);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      chat.removeEventListener("focusin", handleInsideActivity, true);
      chat.removeEventListener("input", handleInsideActivity, true);
      chat.removeEventListener("keydown", handleInsideActivity, true);
      chat.removeEventListener("scroll", handleInsideActivity, true);
      chat.removeEventListener("touchmove", handleInsideActivity, true);
    };
  }, [openedReceiptCode]);

  useEffect(() => {
    if (!lookup?.expiresAt) return;
    const expiresAt = new Date(lookup.expiresAt).getTime();
    if (!Number.isFinite(expiresAt)) return;
    let timer = 0;
    const scheduleExpiry = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        setLookup(null);
        setActiveCredential(null);
        setError("Code does not exist.");
        return;
      }
      timer = window.setTimeout(scheduleExpiry, Math.min(remaining, 2_147_000_000));
    };
    scheduleExpiry();
    return () => window.clearTimeout(timer);
  }, [lookup?.expiresAt]);

  const search = async () => {
    if (loading) return;
    const lookupSession = ++lookupSessionRef.current;
    setError("");
    setNotice("");
    setLookup(null);
    setActiveCredential(null);
    setReply("");
    if (!parsedCredential.code) {
      setError("Enter a transmission code.");
      return;
    }
    if (!parsedCredential.generated && !parsedCredential.validShared) {
      setError("Code does not exist.");
      return;
    }
    setLoading(true);
    try {
      const result = await lookupTransmission(parsedCredential.code, parsedCredential.password);
      if (lookupSession !== lookupSessionRef.current) return;
      setLookup(result.transmission);
      setActiveCredential({
        code: parsedCredential.code,
        password: parsedCredential.password,
      });
      setCredential("");
      if (result.transmission.status === "posted") {
        setNotice("This Transmission has been published as Blackbox.");
      } else if ((result.transmission.thread || []).some((item) => item.author === "presence")) {
        setNotice("A reply has been received.");
      }
    } catch (err: any) {
      setError(err?.message || "Code does not exist.");
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async () => {
    const cleanReply = reply.trim();
    if (!lookup || !activeCredential) return;
    const lookupSession = lookupSessionRef.current;
    if (cleanReply.length < 2) {
      setError("Write at least 2 characters.");
      return;
    }
    setError("");
    setNotice("");
    setReplying(true);
    try {
      const result = await replyToTransmission(activeCredential.code, activeCredential.password, cleanReply);
      if (lookupSession !== lookupSessionRef.current) return;
      setLookup(result.transmission);
      setReply("");
      setNotice(result.message || "Reply received by the Blackbox.");
    } catch (err: any) {
      if (lookupSession !== lookupSessionRef.current) return;
      setLookup(null);
      setActiveCredential(null);
      setError(err?.message || "Reply failed.");
    } finally {
      setReplying(false);
    }
  };

  return (
    <div className="bs-blackbox-receipt-lookup">
      <div className="bs-blackbox-receipt-heading">
        <span className="bs-blackbox-receipt-icon" aria-hidden="true"><Search size={15} /></span>
        <div>
          <strong>Track a transmission</strong>
          <p id="bs-blackbox-tracking-guidance" aria-live="polite">{trackingGuidance}</p>
        </div>
      </div>

      <div ref={lookupRowRef} className="bs-blackbox-lookup-row mt-4 flex gap-2">
        <input
          value={credential}
          onChange={(event) => {
            setCredential(event.target.value.toUpperCase().replace(/[^A-Z0-9+-]/g, "").slice(0, 55));
            setActiveCredential(null);
            setReply("");
            if (error) setError("");
            if (notice) setNotice("");
            if (lookup) setLookup(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void search();
            }
          }}
          name="blackbox-tracking-credential"
          disabled={loading}
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore="true"
          data-bwignore="true"
          data-lpignore="true"
          aria-label="Transmission code or shared code plus password"
          aria-describedby="bs-blackbox-tracking-guidance"
          className="bs-blackbox-control h-10 min-w-0 flex-1 rounded-md border border-white/10 bg-black px-3 font-mono text-sm text-white outline-none transition"
        />
        <button
          onClick={search}
          disabled={loading}
          className="bs-blackbox-lookup-button inline-flex h-10 items-center justify-center rounded-md px-3 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Look up receipt"
          title="Look up receipt"
        >
          {loading ? <Clock size={16} /> : <Search size={16} />}
        </button>
      </div>

      {notice && <div className="bs-blackbox-alert is-success mt-3 rounded-md border px-3 py-2 text-xs" role="status">{notice}</div>}
      {error && <div className="bs-blackbox-alert is-error mt-3 rounded-md border px-3 py-2 text-xs" role="alert">{error}</div>}
      {lookup && (
        <div ref={lookupResultRef} className="bs-blackbox-result bs-blackbox-chat mt-3 overflow-hidden rounded-md border border-white/10">
          <header className="bs-blackbox-chat-header">
            <div className="bs-blackbox-chat-identity">
              <span className="bs-blackbox-chat-avatar" aria-hidden="true"><Package size={16} strokeWidth={1.7} /></span>
              <div className="bs-blackbox-chat-title">
                <strong>Blackbox Transmission</strong>
                <span className="font-mono">{lookup.receiptCode}</span>
              </div>
            </div>
          </header>

          <div className="bs-blackbox-chat-meta">
            <span>{lookup.category}</span>
            <span>{lookup.accessMode === "read_only" ? "Receiver read-only" : "Two-way access"}</span>
            {lookup.expiresAt && <span>Until {formatReceiptDate(lookup.expiresAt)}</span>}
          </div>

          <div ref={chatStreamRef} className="bs-blackbox-chat-stream" aria-label="Blackbox conversation" role="log">
            {lookup.message && (
              <div className={`bs-blackbox-chat-line is-sender ${lookup.viewerRole === "sender" ? "is-current" : "is-other"}`}>
                <div className="bs-blackbox-chat-bubble">
                  <div className="bs-blackbox-chat-bubble-head">
                    <span>{lookup.viewerRole === "sender" ? "You · Sender" : "Sender"}</span>
                    <time>{formatReceiptDate(lookup.createdAt)}</time>
                  </div>
                  <p>{lookup.message}</p>
                </div>
              </div>
            )}

            {(lookup.thread || []).map((entry) => {
              const entryRole = entry.author === "presence"
                ? "blackbox"
                : entry.participantRole === "creator"
                  ? "sender"
                  : entry.participantRole === "participant"
                    ? "receiver"
                    : entry.participantRole || "sender";
              const isCurrent = entryRole === lookup.viewerRole;
              const roleLabel = entryRole === "blackbox"
                ? "Blackbox"
                : entryRole === "sender"
                  ? "Sender"
                  : "Receiver";
              return (
                <div
                  key={entry.id}
                  className={`bs-blackbox-chat-line is-${entryRole} ${isCurrent ? "is-current" : "is-other"}`}
                >
                  <div className="bs-blackbox-chat-bubble">
                    <div className="bs-blackbox-chat-bubble-head">
                      <span>{isCurrent ? `You · ${roleLabel}` : roleLabel}</span>
                      <time>{formatReceiptDate(entry.createdAt)}</time>
                    </div>
                    <p>{entry.message}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {lookup.canReply ? (
            <div className="bs-blackbox-chat-composer">
              <textarea
                value={reply}
                onChange={(event) => {
                  setReply(event.target.value.slice(0, 3000));
                  if (error) setError("");
                }}
                placeholder={lookup.canAppeal ? "Appeal or add more context…" : `Reply as ${lookup.viewerRole === "receiver" ? "Receiver" : "Sender"}…`}
                rows={2}
                aria-label={`Reply as ${lookup.viewerRole === "receiver" ? "receiver" : "sender"}`}
                className="bs-blackbox-chat-input"
              />
              <button
                onClick={sendReply}
                disabled={replying || reply.trim().length < 2}
                className="bs-blackbox-chat-send"
                aria-label={lookup.canAppeal ? "Send appeal" : "Send reply"}
                title={lookup.canAppeal ? "Send appeal" : "Send reply"}
              >
                {replying ? <Clock size={15} /> : <Send size={15} />}
              </button>
            </div>
          ) : lookup.viewerRole === "receiver" && lookup.accessMode === "read_only" ? null : (
            <div className="bs-blackbox-closed bs-blackbox-chat-closed">
              {lookup.canReplyReason || "This case is closed. No further replies can be added."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatReceiptDate(value: string) {
  return formatBlackspaceFullDateTime(value, "Unknown");
}

function PublicPageStyles() {
  return (
    <style>{`
      .blackspace-public {
        color-scheme: dark;
        isolation: isolate;
        min-height: 100dvh;
        overflow: hidden;
        background: #03040b;
      }

      html[class] .blackspace-public {
        background: #03040b !important;
        color: #f8fafc !important;
      }

      html[class] .blackspace-public .text-white,
      html[class] .blackspace-public .text-neutral-100,
      html[class] .blackspace-public .text-neutral-200 {
        color: #f8fafc !important;
      }

      html[class] .blackspace-public .text-neutral-300,
      html[class] .blackspace-public .text-neutral-400 {
        color: rgba(212, 212, 216, 0.92) !important;
      }

      html[class] .blackspace-public .text-neutral-500 {
        color: rgba(161, 161, 170, 0.86) !important;
      }

      html[class] .blackspace-public .bg-black,
      html[class] .blackspace-public .bg-gray-900 {
        background-color: rgba(0, 0, 0, 0.76) !important;
      }

      html[class] .blackspace-public .bg-neutral-950,
      html[class] .blackspace-public .bg-neutral-950\\/78,
      html[class] .blackspace-public .bg-neutral-950\\/80,
      html[class] .blackspace-public .bg-neutral-900 {
        background-color: rgba(3, 7, 18, 0.84) !important;
      }

      html[class] .blackspace-public input,
      html[class] .blackspace-public textarea {
        background-color: rgba(0, 0, 0, 0.82) !important;
        color: #f8fafc !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
      }

      html[class] .blackspace-auth-shell .bg-black,
      html[class] .blackspace-auth-shell .bg-gray-900,
      html[class] .blackspace-auth-shell .bg-gray-800 {
        background-color: rgba(0, 0, 0, 0.64) !important;
      }

      html[class] .blackspace-auth-shell .text-white,
      html[class] .blackspace-auth-shell .text-gray-300 {
        color: #f8fafc !important;
      }

      html[class] .blackspace-auth-shell .text-gray-400,
      html[class] .blackspace-auth-shell .text-gray-500 {
        color: #a1a1aa !important;
      }

      html[class] .blackspace-auth-shell input {
        background-color: rgba(255, 255, 255, 0.06) !important;
        color: #f8fafc !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
      }

      .blackspace-public::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        opacity: 0.32;
        background-image:
          linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px);
        background-size: 72px 72px;
      }

      .blackspace-public::after {
        content: "";
        position: absolute;
        inset: -18%;
        z-index: 0;
        pointer-events: none;
        opacity: 0.42;
        mix-blend-mode: screen;
        background:
          linear-gradient(
            115deg,
            rgba(34, 211, 238, 0.0) 0%,
            rgba(34, 211, 238, 0.36) 8%,
            rgba(20, 184, 166, 0.32) 16%,
            rgba(74, 222, 128, 0.3) 24%,
            rgba(250, 204, 21, 0.32) 32%,
            rgba(251, 113, 133, 0.34) 40%,
            rgba(236, 72, 153, 0.34) 48%,
            rgba(168, 85, 247, 0.36) 56%,
            rgba(99, 102, 241, 0.34) 64%,
            rgba(59, 130, 246, 0.34) 72%,
            rgba(14, 165, 233, 0.34) 80%,
            rgba(45, 212, 191, 0.32) 88%,
            rgba(34, 211, 238, 0.0) 100%
          );
        background-size: 900% 900%;
      }

      .bs-logo-signal {
        animation: bsLogoSignal 9s ease-in-out infinite, bsLogoHue 82s linear infinite;
      }

      .bs-public-logo-frame,
      html[class] .blackspace-public .bs-public-logo-frame {
        position: relative;
        overflow: hidden;
        border: 0 !important;
        padding: 1px;
        background: transparent !important;
        box-shadow:
          inset 0 0 0 0.5px rgba(103, 232, 249, 0.42),
          0 0 16px rgba(34, 211, 238, 0.16),
          0 0 34px rgba(217, 70, 239, 0.10) !important;
      }

      .bs-public-logo-frame::after,
      html[class] .blackspace-public .bs-public-logo-frame::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;
        box-shadow: inset 0 0 0 0.5px currentColor;
        color: rgba(103, 232, 249, 0.46);
      }

      .bs-public-logo-image,
      html[class] .blackspace-public .bs-public-logo-image {
        display: block;
        border-radius: inherit;
        filter:
          drop-shadow(0 0 7px rgba(34, 211, 238, 0.16))
          drop-shadow(0 0 14px rgba(217, 70, 239, 0.08));
      }

      .bs-public-login-button,
      html[class] .blackspace-public .bs-public-login-button {
        position: relative;
        width: 2.5rem;
        justify-content: center;
        overflow: visible;
        padding-inline: 0 !important;
        border-color: rgba(34, 211, 238, 0.26) !important;
        color: rgba(207, 250, 254, 0.96) !important;
        background:
          linear-gradient(135deg, rgba(34, 211, 238, 0.10), rgba(168, 85, 247, 0.08)),
          rgba(0, 0, 0, 0.36) !important;
        box-shadow: 0 0 22px rgba(34, 211, 238, 0.08);
      }

      .bs-public-login-button:hover,
      html[class] .blackspace-public .bs-public-login-button:hover {
        border-color: rgba(34, 211, 238, 0.54) !important;
        color: #ffffff !important;
        background:
          linear-gradient(135deg, rgba(34, 211, 238, 0.18), rgba(217, 70, 239, 0.10)),
          rgba(0, 0, 0, 0.42) !important;
      }

      .bs-public-signup-button,
      html[class] .blackspace-public .bs-public-signup-button {
        position: relative;
        width: 2.5rem;
        justify-content: center;
        overflow: visible;
        padding-inline: 0 !important;
        color: #020617 !important;
        background:
          linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(207, 250, 254, 0.94)) !important;
        box-shadow: 0 0 22px rgba(34, 211, 238, 0.10);
      }

      .bs-public-signup-button:hover,
      html[class] .blackspace-public .bs-public-signup-button:hover {
        color: #020617 !important;
        background:
          linear-gradient(135deg, rgba(236, 254, 255, 1), rgba(252, 231, 243, 0.94)) !important;
      }

      .bs-public-brand-link:focus-visible,
      .bs-public-login-button:focus-visible,
      .bs-public-signup-button:focus-visible,
      .bs-public-action-card:focus-visible {
        outline: 2px solid #a5f3fc;
        outline-offset: 3px;
      }

      .blackspace-auth-card,
      .blackspace-auth-card * {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .blackspace-auth-card::-webkit-scrollbar,
      .blackspace-auth-card *::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }

      .bs-public-header-tooltip {
        position: absolute;
        right: 0;
        top: calc(100% + 0.5rem);
        z-index: 30;
        width: max-content;
        max-width: 10rem;
        border: 1px solid rgba(167, 139, 250, 0.24);
        border-radius: 0.42rem;
        background: rgba(5, 7, 16, 0.96);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.42), 0 0 20px rgba(139, 92, 246, 0.10);
        color: rgba(248, 250, 252, 0.94);
        font-size: 0.68rem;
        font-weight: 750;
        line-height: 1;
        opacity: 0;
        padding: 0.5rem 0.62rem;
        pointer-events: none;
        transform: translateY(-0.3rem);
        transition: opacity 150ms ease, transform 150ms ease;
        white-space: nowrap;
      }

      .bs-public-login-button:hover .bs-public-header-tooltip,
      .bs-public-login-button:focus-visible .bs-public-header-tooltip,
      .bs-public-signup-button:hover .bs-public-header-tooltip,
      .bs-public-signup-button:focus-visible .bs-public-header-tooltip,
      .bs-public-header-tooltip.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .bs-public-copy-mobile,
      .bs-public-action-text small,
      .bs-public-action-arrow {
        display: none;
      }

      .bs-public-action-icon,
      .bs-public-action-text {
        display: inline-flex;
        align-items: center;
      }

      .bs-public-awake-signal {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .bs-public-side-note,
      .bs-public-horizon,
      .bs-public-ripple {
        display: none;
      }

      .bs-section-modal-backdrop {
        background:
          radial-gradient(circle at 50% 18%, rgba(65, 50, 139, .15), transparent 32rem),
          rgba(1, 2, 7, .86) !important;
        backdrop-filter: blur(18px) saturate(1.08) !important;
      }

      .bs-section-modal-panel {
        position: relative;
        color-scheme: dark;
        background: linear-gradient(155deg, rgba(10, 12, 23, .98), rgba(3, 5, 11, .99)) !important;
        border-color: rgba(255, 255, 255, .09) !important;
        box-shadow: 0 34px 100px rgba(0, 0, 0, .72), 0 0 0 1px rgba(255,255,255,.018) inset !important;
      }

      .bs-blackbox-modal {
        width: min(64rem, calc(100vw - 2rem)) !important;
        max-width: 64rem !important;
        max-height: min(52rem, calc(100dvh - 2rem));
        border-color: rgba(139, 92, 246, .18) !important;
        border-radius: 1.1rem !important;
        background:
          radial-gradient(circle at 83% 0%, rgba(111, 71, 206, .10), transparent 24rem),
          radial-gradient(circle at 8% 34%, rgba(34, 211, 238, .045), transparent 22rem),
          linear-gradient(155deg, rgba(8, 10, 20, .99), rgba(2, 4, 10, .995)) !important;
        overflow: hidden;
      }

      .bs-blackbox-signal-line {
        position: absolute;
        inset: 0 0 auto;
        z-index: 3;
        height: 1px;
        background: linear-gradient(90deg, transparent 7%, rgba(34, 211, 238, .62) 29%, rgba(139, 92, 246, .72) 67%, transparent 94%);
        box-shadow: 0 0 20px rgba(109, 80, 220, .22);
      }

      .bs-section-modal-header {
        min-height: 5.4rem;
        padding: 1.05rem 1.3rem !important;
        border-color: rgba(255, 255, 255, .07) !important;
        background: rgba(5, 7, 15, .72);
      }

      .bs-section-modal-heading {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: .9rem;
      }

      .bs-section-modal-heading h2 {
        margin: .08rem 0 0;
        color: rgba(248, 250, 252, .98) !important;
        font-size: 1.16rem !important;
        line-height: 1.2;
        letter-spacing: -.025em;
      }

      .bs-section-modal-heading p {
        margin-top: .28rem;
        color: rgba(161, 170, 202, .74);
        font-size: .7rem;
        font-weight: 560;
        line-height: 1.35;
      }

      .bs-blackbox-mark,
      .bs-blackbox-receipt-icon {
        display: grid;
        flex: 0 0 auto;
        place-items: center;
        border: 1px solid rgba(139, 92, 246, .24);
        color: #b9a4ff;
        background: linear-gradient(145deg, rgba(139, 92, 246, .14), rgba(34, 211, 238, .035));
        box-shadow: inset 0 1px rgba(255,255,255,.035), 0 0 24px rgba(105, 74, 214, .08);
      }

      .bs-blackbox-mark {
        width: 2.65rem;
        height: 2.65rem;
        border-radius: .72rem;
      }

      .bs-blackbox-field-label {
        display: block;
        color: rgba(181, 190, 219, .74);
        font-size: .66rem;
        font-style: normal;
        font-weight: 680;
        letter-spacing: .025em;
        line-height: 1.3;
        text-transform: none;
      }

      .bs-section-modal-close,
      html[class] .blackspace-public .bs-section-modal-close {
        flex: 0 0 auto;
        border-color: rgba(255, 255, 255, .08) !important;
        border-radius: .65rem !important;
        color: rgba(176, 181, 203, .78) !important;
        background: rgba(255, 255, 255, .025) !important;
      }

      .bs-section-modal-close:hover,
      html[class] .blackspace-public .bs-section-modal-close:hover {
        border-color: rgba(167, 139, 250, .24) !important;
        color: #fff !important;
        background: rgba(139, 92, 246, .08) !important;
      }

      .bs-section-modal-close:focus-visible {
        outline: 2px solid #a5f3fc;
        outline-offset: 2px;
      }

      .bs-blackbox-modal .bs-section-modal-body {
        max-height: calc(100dvh - 7.4rem) !important;
        overflow-x: hidden;
        overflow-y: auto !important;
        padding: 1.25rem !important;
        scrollbar-color: rgba(139, 92, 246, .26) transparent;
        -webkit-overflow-scrolling: touch;
      }

      .bs-blackbox-workspace {
        align-items: start;
        grid-template-columns: minmax(0, 1.14fr) minmax(18rem, .86fr) !important;
        gap: 1rem !important;
      }

      .bs-blackbox-composer,
      .bs-blackbox-receipt-panel {
        border: 1px solid rgba(255, 255, 255, .075) !important;
        border-radius: .9rem !important;
        background: rgba(255, 255, 255, .018) !important;
        box-shadow: inset 0 1px rgba(255,255,255,.018);
      }

      .bs-blackbox-composer {
        padding: 1rem;
      }

      .bs-blackbox-receipt-panel {
        position: sticky;
        top: 0;
        min-width: 0;
        padding: 1rem !important;
        background:
          radial-gradient(circle at 100% 0%, rgba(139,92,246,.075), transparent 13rem),
          rgba(255, 255, 255, .016) !important;
      }

      .bs-blackbox-access-setup {
        display: grid;
        gap: .78rem;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid rgba(255,255,255,.065);
      }

      .bs-blackbox-receipt-heading strong {
        display: block;
        color: rgba(242, 245, 255, .96);
        font-size: .76rem;
        font-weight: 850;
        line-height: 1.3;
      }

      .bs-blackbox-receipt-heading p {
        margin-top: .22rem;
        color: rgba(163, 171, 202, .68);
        font-size: .65rem;
        font-weight: 540;
        line-height: 1.55;
      }

      .bs-blackbox-field-group {
        display: grid;
        gap: .48rem;
      }

      .bs-blackbox-field-label {
        padding-left: .12rem;
      }

      .bs-blackbox-field-label em {
        margin-left: .45rem;
        color: rgba(123, 132, 162, .56);
        font-size: .55rem;
        font-style: normal;
        letter-spacing: .04em;
      }

      .bs-blackbox-code-mode,
      .bs-blackbox-access-mode {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: .28rem;
        padding: .25rem;
        border: 1px solid rgba(255,255,255,.07);
        border-radius: .68rem;
        background: rgba(0,0,0,.28);
      }

      .bs-blackbox-code-mode button,
      .bs-blackbox-access-mode button,
      html[class] .blackspace-public .bs-blackbox-code-mode button,
      html[class] .blackspace-public .bs-blackbox-access-mode button {
        min-height: 2.15rem;
        border: 1px solid transparent !important;
        border-radius: .5rem !important;
        color: rgba(151,160,190,.72) !important;
        background: transparent !important;
        font-size: .63rem;
        font-weight: 780;
        transition: border-color 160ms ease, background 160ms ease, color 160ms ease, box-shadow 160ms ease;
      }

      .bs-blackbox-code-mode button:hover,
      .bs-blackbox-access-mode button:hover,
      html[class] .blackspace-public .bs-blackbox-code-mode button:hover,
      html[class] .blackspace-public .bs-blackbox-access-mode button:hover {
        color: rgba(238,241,251,.94) !important;
        background: rgba(255,255,255,.035) !important;
      }

      .bs-blackbox-code-mode button.is-active,
      .bs-blackbox-access-mode button.is-active,
      html[class] .blackspace-public .bs-blackbox-code-mode button.is-active,
      html[class] .blackspace-public .bs-blackbox-access-mode button.is-active {
        border-color: rgba(103,232,249,.2) !important;
        color: #dcfbff !important;
        background: linear-gradient(135deg, rgba(34,211,238,.085), rgba(139,92,246,.075)) !important;
        box-shadow: inset 0 1px rgba(255,255,255,.03), 0 0 18px rgba(34,211,238,.035) !important;
      }

      .bs-blackbox-shared-settings {
        display: grid;
        gap: .58rem;
        padding: .65rem;
        border: 1px solid rgba(139,92,246,.13);
        border-radius: .76rem;
        background: linear-gradient(145deg, rgba(139,92,246,.04), rgba(34,211,238,.018));
      }

      .bs-blackbox-shared-settings small {
        color: rgba(126,135,164,.68);
        font-size: .58rem;
        font-weight: 560;
        line-height: 1.45;
      }

      .bs-blackbox-code-state {
        display: inline-flex;
        width: fit-content;
        align-items: center;
        gap: .34rem;
        min-height: 1.25rem;
        border-radius: 999px;
        padding: .2rem .5rem;
        font-size: .56rem;
        font-weight: 820;
        letter-spacing: .02em;
      }

      .bs-blackbox-code-state.is-checking {
        color: rgba(203, 213, 225, .8);
        background: rgba(148, 163, 184, .08);
      }

      .bs-blackbox-code-state.is-available {
        color: #bbf7d0;
        background: rgba(34, 197, 94, .11);
      }

      .bs-blackbox-code-state.is-failed {
        color: #fecaca;
        background: rgba(239, 68, 68, .11);
      }

      .bs-blackbox-password-intro {
        display: flex;
        align-items: flex-start;
        gap: .62rem;
        border-top: 1px solid rgba(255,255,255,.055);
        padding-top: .78rem;
      }

      .bs-blackbox-password-icon {
        display: grid;
        width: 2rem;
        height: 2rem;
        flex: 0 0 2rem;
        place-items: center;
        border: 1px solid rgba(167,139,250,.2);
        border-radius: .58rem;
        color: #c4b5fd;
        background: rgba(139,92,246,.09);
      }

      .bs-blackbox-password-intro strong {
        display: block;
        color: rgba(241,245,249,.94);
        font-size: .68rem;
        font-weight: 850;
      }

      .bs-blackbox-password-intro p {
        margin-top: .2rem;
        color: rgba(148,163,184,.7);
        font-size: .58rem;
        line-height: 1.45;
      }

      .bs-blackbox-password-pairs {
        display: grid;
        gap: .4rem;
      }

      .bs-blackbox-password-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(10.5rem, .95fr);
        align-items: center;
        gap: .7rem;
        min-width: 0;
        border: 1px solid rgba(255,255,255,.065);
        border-radius: .65rem;
        padding: .45rem .52rem;
        background: rgba(0,0,0,.2);
      }

      .bs-blackbox-password-row.is-sender {
        border-color: rgba(139,92,246,.18);
        background: linear-gradient(135deg, rgba(139,92,246,.065), rgba(0,0,0,.18));
      }

      .bs-blackbox-password-row.is-receiver {
        border-color: rgba(34,211,238,.15);
        background: linear-gradient(135deg, rgba(34,211,238,.045), rgba(0,0,0,.18));
      }

      .bs-blackbox-password-title {
        display: flex;
        align-items: center;
        gap: .42rem;
        color: rgba(226,232,240,.88);
        font-size: .6rem;
        font-weight: 840;
      }

      .bs-blackbox-password-row.is-sender .bs-blackbox-password-title { color: #ddd6fe; }
      .bs-blackbox-password-row.is-receiver .bs-blackbox-password-title { color: #cffafe; }

      .bs-blackbox-password-input-shell {
        position: relative;
        min-width: 0;
      }

      .bs-blackbox-password-input-shell input {
        height: 2.25rem !important;
        min-height: 2.25rem;
        padding-right: 2.35rem !important;
        color: rgba(248,250,252,.98) !important;
        opacity: 1;
        caret-color: #c4b5fd;
        -webkit-text-fill-color: rgba(248,250,252,.98);
      }

      .bs-blackbox-password-input-shell button,
      html[class] .blackspace-public .bs-blackbox-password-input-shell button {
        position: absolute;
        top: 50%;
        right: .35rem;
        display: grid;
        width: 1.75rem;
        height: 1.75rem;
        place-items: center;
        transform: translateY(-50%);
        border: 0 !important;
        border-radius: .45rem !important;
        color: rgba(196,181,253,.72) !important;
        background: transparent !important;
      }

      .bs-blackbox-password-input-shell button:hover,
      html[class] .blackspace-public .bs-blackbox-password-input-shell button:hover {
        color: #fff !important;
        background: rgba(139,92,246,.11) !important;
      }

      .bs-blackbox-shared-settings-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: .4rem;
      }

      .bs-blackbox-compact-setting {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: .65rem;
        min-height: 2.5rem;
        border-top: 1px solid rgba(255,255,255,.05);
        padding: .4rem .08rem 0;
      }

      .bs-blackbox-compact-copy {
        display: grid;
        min-width: 0;
        gap: .18rem;
        padding-left: .12rem;
      }

      .bs-blackbox-selection-hint {
        overflow: hidden;
        padding-left: .45rem;
        color: rgba(174,182,210,.7);
        font-size: .56rem;
        font-weight: 650;
        line-height: 1.2;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .bs-blackbox-icon-options {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: .28rem;
      }

      .bs-blackbox-icon-options button,
      html[class] .blackspace-public .bs-blackbox-icon-options button {
        display: grid;
        width: 2.08rem;
        height: 2.08rem;
        flex: 0 0 2.08rem;
        place-items: center;
        border: 1px solid rgba(255,255,255,.075) !important;
        border-radius: .56rem !important;
        padding: 0 !important;
        color: rgba(155,164,193,.76) !important;
        background: rgba(1,3,8,.5) !important;
        transition: border-color 160ms ease, background 160ms ease, color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
      }

      .bs-blackbox-icon-options button:hover,
      html[class] .blackspace-public .bs-blackbox-icon-options button:hover {
        border-color: rgba(167,139,250,.26) !important;
        color: rgba(239,235,255,.94) !important;
        background: rgba(139,92,246,.07) !important;
      }

      .bs-blackbox-icon-options button.is-active,
      html[class] .blackspace-public .bs-blackbox-icon-options button.is-active {
        border-color: rgba(167,139,250,.42) !important;
        color: #f2edff !important;
        background: linear-gradient(145deg, rgba(139,92,246,.18), rgba(34,211,238,.07)) !important;
        box-shadow: inset 0 1px rgba(255,255,255,.045), 0 0 18px rgba(120,83,220,.09) !important;
        transform: translateY(-1px);
      }

      .bs-blackbox-icon-options.is-access button {
        width: 2.2rem;
        flex-basis: 2.2rem;
      }

      .bs-blackbox-categories {
        gap: .42rem !important;
      }

      .bs-blackbox-category,
      html[class] .blackspace-public .bs-blackbox-category {
        --category-rgb: 103, 232, 249;
        --category-text: #d9fbff;
        height: 2.25rem !important;
        border-color: rgba(255,255,255,.075) !important;
        border-radius: .58rem !important;
        color: rgba(158, 167, 197, .76) !important;
        background: rgba(2, 4, 10, .46) !important;
        font-size: .62rem !important;
        font-weight: 780 !important;
      }

      .bs-blackbox-category.tone-cyan,
      .bs-blackbox-message.tone-cyan,
      html[class] .blackspace-public .bs-blackbox-category.tone-cyan {
        --category-rgb: 103, 232, 249;
        --category-text: #d9fbff;
      }

      .bs-blackbox-category.tone-rose,
      .bs-blackbox-message.tone-rose,
      html[class] .blackspace-public .bs-blackbox-category.tone-rose {
        --category-rgb: 251, 113, 133;
        --category-text: #ffe4e8;
      }

      .bs-blackbox-category.tone-amber,
      .bs-blackbox-message.tone-amber,
      html[class] .blackspace-public .bs-blackbox-category.tone-amber {
        --category-rgb: 251, 191, 36;
        --category-text: #fff1c2;
      }

      .bs-blackbox-category.tone-violet,
      .bs-blackbox-message.tone-violet,
      html[class] .blackspace-public .bs-blackbox-category.tone-violet {
        --category-rgb: 167, 139, 250;
        --category-text: #eee8ff;
      }

      .bs-blackbox-category.tone-emerald,
      .bs-blackbox-message.tone-emerald,
      html[class] .blackspace-public .bs-blackbox-category.tone-emerald {
        --category-rgb: 52, 211, 153;
        --category-text: #d8fff0;
      }

      .bs-blackbox-category:hover,
      html[class] .blackspace-public .bs-blackbox-category:hover {
        border-color: rgba(var(--category-rgb), .38) !important;
        color: var(--category-text) !important;
        background: rgba(var(--category-rgb), .09) !important;
        box-shadow: inset 0 0 0 1px rgba(var(--category-rgb), .025), 0 0 16px rgba(var(--category-rgb), .045) !important;
      }

      .bs-blackbox-category.is-active,
      html[class] .blackspace-public .bs-blackbox-category.is-active {
        border-color: rgba(var(--category-rgb), .58) !important;
        color: var(--category-text) !important;
        background: linear-gradient(135deg, rgba(var(--category-rgb), .18), rgba(var(--category-rgb), .09)) !important;
        box-shadow: inset 0 0 0 1px rgba(var(--category-rgb), .055), 0 0 22px rgba(var(--category-rgb), .11) !important;
      }

      .bs-blackbox-control,
      html[class] .blackspace-public .bs-blackbox-control {
        border: 1px solid rgba(255,255,255,.085) !important;
        border-radius: .68rem !important;
        color: rgba(245,247,252,.96) !important;
        background: rgba(1, 3, 8, .72) !important;
        box-shadow: inset 0 1px rgba(255,255,255,.015) !important;
        caret-color: #a5f3fc;
      }

      .bs-blackbox-control:autofill,
      .bs-blackbox-control:-webkit-autofill,
      html[class] .blackspace-public .bs-blackbox-control:autofill,
      html[class] .blackspace-public .bs-blackbox-control:-webkit-autofill {
        border-color: rgba(103, 232, 249, .24) !important;
        color: rgba(245,247,252,.96) !important;
        background: #010308 !important;
        box-shadow: 0 0 0 1000px #010308 inset !important;
        caret-color: #a5f3fc !important;
        -webkit-text-fill-color: rgba(245,247,252,.96) !important;
      }

      .bs-blackbox-control::placeholder {
        color: rgba(116, 124, 151, .55) !important;
      }

      .bs-blackbox-control:focus,
      html[class] .blackspace-public .bs-blackbox-control:focus {
        border-color: rgba(103, 232, 249, .34) !important;
        background: rgba(3, 7, 15, .86) !important;
        box-shadow: 0 0 0 3px rgba(34, 211, 238, .055), 0 0 26px rgba(89, 67, 178, .05) !important;
      }

      .bs-blackbox-message:focus,
      html[class] .blackspace-public .bs-blackbox-message:focus {
        border-color: rgba(var(--category-rgb), .58) !important;
        box-shadow: 0 0 0 3px rgba(var(--category-rgb), .08), 0 0 22px rgba(var(--category-rgb), .11) !important;
        caret-color: rgb(var(--category-rgb));
      }

      .bs-blackbox-message {
        min-height: 9.5rem;
        line-height: 1.65;
      }

      .bs-blackbox-character-count {
        color: rgba(126, 135, 164, .6);
        font-size: .62rem;
        font-weight: 650;
      }

      .bs-blackbox-character-count.is-low {
        color: #f6ca75;
      }

      .bs-blackbox-submit,
      html[class] .blackspace-public .bs-blackbox-submit {
        display: inline-grid;
        grid-template-columns: 2rem max-content;
        align-items: center;
        gap: .62rem;
        min-height: 2.85rem;
        padding: .34rem .7rem .34rem .42rem !important;
        border: 1px solid rgba(103, 232, 249, .34) !important;
        border-radius: .8rem !important;
        color: #eefaff !important;
        background: linear-gradient(110deg, rgba(7, 22, 31, .98), rgba(13, 13, 30, .98) 68%, rgba(22, 13, 40, .98)) !important;
        box-shadow: inset 0 1px rgba(232, 251, 255, .075), 0 9px 24px rgba(0, 0, 0, .28), 0 0 20px rgba(34, 211, 238, .045) !important;
        font-size: .79rem;
        font-weight: 700;
        letter-spacing: .015em;
        transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease, color 180ms ease, transform 180ms ease;
      }

      .bs-blackbox-submit-signal {
        display: grid;
        width: 2rem;
        height: 2rem;
        place-items: center;
        border-radius: .56rem;
        border: 1px solid rgba(103, 232, 249, .2);
        color: #a5f3fc;
        background: rgba(34, 211, 238, .085);
        box-shadow: inset 0 1px rgba(255, 255, 255, .045);
      }

      .bs-blackbox-submit-label { white-space: nowrap; }

      .bs-blackbox-submit:hover:not(:disabled),
      html[class] .blackspace-public .bs-blackbox-submit:hover:not(:disabled) {
        border-color: rgba(103, 232, 249, .64) !important;
        background: linear-gradient(110deg, rgba(10, 32, 42, .98), rgba(19, 19, 41, .98) 68%, rgba(31, 19, 53, .98)) !important;
        box-shadow: inset 0 1px rgba(232, 251, 255, .1), 0 11px 28px rgba(0, 0, 0, .32), 0 0 24px rgba(34, 211, 238, .09) !important;
        transform: translateY(-1px);
      }

      .bs-blackbox-submit:focus-visible {
        outline: 2px solid rgba(165,243,252,.72);
        outline-offset: 2px;
      }

      .bs-blackbox-submit:disabled,
      html[class] .blackspace-public .bs-blackbox-submit:disabled {
        cursor: not-allowed;
        border-color: rgba(130, 139, 169, .18) !important;
        color: rgba(169, 181, 204, .62) !important;
        background: linear-gradient(110deg, rgba(8, 13, 21, .88), rgba(13, 12, 25, .88)) !important;
        box-shadow: inset 0 1px rgba(255, 255, 255, .025) !important;
        opacity: 1 !important;
        transform: none;
      }

      .bs-blackbox-submit:disabled .bs-blackbox-submit-signal {
        border-color: rgba(130, 139, 169, .12);
        color: rgba(165, 174, 199, .4);
        background: rgba(130, 139, 169, .035);
      }

      .bs-blackbox-receipt-heading {
        display: flex;
        align-items: center;
        gap: .7rem;
      }

      .bs-blackbox-receipt-icon {
        width: 2.2rem;
        height: 2.2rem;
        border-radius: .65rem;
      }

      .bs-blackbox-lookup-button,
      html[class] .blackspace-public .bs-blackbox-lookup-button {
        width: 2.5rem;
        flex: 0 0 2.5rem;
        border: 1px solid rgba(167,139,250,.24) !important;
        border-radius: .65rem !important;
        color: #e9e2ff !important;
        background: rgba(139,92,246,.12) !important;
      }

      .bs-blackbox-lookup-button:hover,
      html[class] .blackspace-public .bs-blackbox-lookup-button:hover {
        border-color: rgba(196,181,253,.42) !important;
        background: rgba(139,92,246,.2) !important;
      }

      .bs-blackbox-alert {
        line-height: 1.5;
      }

      .bs-blackbox-alert.is-error {
        border-color: rgba(248,113,113,.2) !important;
        color: #fecaca !important;
        background: rgba(127,29,29,.12) !important;
      }

      .bs-blackbox-alert.is-success {
        border-color: rgba(52,211,153,.2) !important;
        color: #d1fae5 !important;
        background: rgba(6,95,70,.12) !important;
      }

      .bs-blackbox-receipt-code-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: .35rem .7rem;
        margin-top: .45rem;
      }

      .bs-blackbox-receipt-code-row p {
        min-width: 0;
        font-size: .72rem;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }

      .bs-blackbox-receipt-code-row code {
        color: #f5f3ff;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        user-select: all;
      }

      .bs-blackbox-copy-button,
      html[class] .blackspace-public .bs-blackbox-copy-button {
        display: inline-flex;
        min-height: 1.85rem;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        gap: .35rem;
        border: 1px solid rgba(103,232,249,.26) !important;
        border-radius: .48rem !important;
        padding: .25rem .55rem !important;
        color: #d9fbff !important;
        background: linear-gradient(135deg, rgba(34,211,238,.1), rgba(139,92,246,.12)) !important;
        font-size: .65rem;
        font-weight: 750;
      }

      .bs-blackbox-copy-button:hover,
      html[class] .blackspace-public .bs-blackbox-copy-button:hover {
        border-color: rgba(165,243,252,.52) !important;
        background: linear-gradient(135deg, rgba(34,211,238,.16), rgba(139,92,246,.18)) !important;
      }

      .bs-blackbox-copy-button:focus-visible {
        outline: 2px solid rgba(165,243,252,.75);
        outline-offset: 2px;
      }

      .bs-blackbox-copy-error {
        margin-top: .45rem;
        color: #fecaca;
        font-size: .65rem;
        line-height: 1.35;
      }

      .bs-blackbox-result {
        border-color: rgba(255,255,255,.075) !important;
        background: rgba(1,3,8,.46) !important;
      }

      .bs-blackbox-chat {
        border-radius: .82rem !important;
        background:
          radial-gradient(circle at 100% 0%, rgba(139,92,246,.09), transparent 12rem),
          rgba(1,3,8,.78) !important;
        box-shadow: 0 18px 45px rgba(0,0,0,.26);
      }

      .bs-blackbox-chat-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: .7rem;
        border-bottom: 1px solid rgba(255,255,255,.065);
        padding: .72rem;
        background: rgba(255,255,255,.018);
      }

      .bs-blackbox-chat-identity {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: .6rem;
      }

      .bs-blackbox-chat-avatar {
        display: grid;
        width: 2rem;
        height: 2rem;
        min-width: 2rem;
        min-height: 2rem;
        flex: 0 0 2rem;
        aspect-ratio: 1;
        place-items: center;
        border: 1px solid rgba(167,139,250,.24);
        border-radius: .52rem;
        color: #c4b5fd;
        background: linear-gradient(145deg, rgba(139,92,246,.16), rgba(34,211,238,.045));
        box-shadow: inset 0 1px rgba(255,255,255,.035), 0 0 18px rgba(139,92,246,.055);
      }

      .bs-blackbox-chat-title {
        display: flex;
        min-width: 0;
        flex-direction: column;
        justify-content: center;
        gap: .16rem;
      }

      .bs-blackbox-chat-title strong,
      .bs-blackbox-chat-title > span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .bs-blackbox-chat-title strong {
        color: rgba(248,250,252,.96);
        font-size: .76rem;
        font-weight: 800;
        line-height: 1.2;
      }

      .bs-blackbox-chat-title > span {
        margin: 0;
        color: rgba(160,174,205,.72);
        font-size: .59rem;
        line-height: 1.2;
      }

      .bs-blackbox-chat-meta {
        display: flex;
        flex-wrap: wrap;
        gap: .3rem;
        border-bottom: 1px solid rgba(255,255,255,.05);
        padding: .5rem .72rem;
      }

      .bs-blackbox-chat-meta span {
        border-radius: 999px;
        padding: .2rem .45rem;
        color: rgba(148,163,184,.72);
        background: rgba(255,255,255,.028);
        font-size: .5rem;
        font-weight: 720;
      }

      .bs-blackbox-chat-stream {
        display: flex;
        min-height: 0;
        max-height: clamp(10rem, 30dvh, 12.5rem);
        flex-direction: column;
        gap: .58rem;
        overflow-y: auto;
        padding: .82rem .7rem;
        background:
          linear-gradient(rgba(255,255,255,.012) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.01) 1px, transparent 1px);
        background-size: 22px 22px;
        scrollbar-color: rgba(167,139,250,.55) transparent;
        scrollbar-width: thin;
      }

      .bs-blackbox-chat-stream::-webkit-scrollbar { width: 6px; }
      .bs-blackbox-chat-stream::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: rgba(167,139,250,.5);
      }

      .bs-blackbox-chat-line {
        display: flex;
        width: 100%;
        flex: 0 0 auto;
      }

      .bs-blackbox-chat-line.is-current { justify-content: flex-end; }
      .bs-blackbox-chat-line.is-other { justify-content: flex-start; }

      .bs-blackbox-chat-bubble {
        width: fit-content;
        max-width: 82%;
        border: 1px solid rgba(255,255,255,.07);
        border-radius: .78rem .78rem .78rem .24rem;
        padding: .56rem .65rem;
        color: rgba(241,245,249,.92);
        background: rgba(30,41,59,.38);
        box-shadow: 0 8px 22px rgba(0,0,0,.16);
      }

      .bs-blackbox-chat-line.is-current .bs-blackbox-chat-bubble {
        border-color: rgba(167,139,250,.25);
        border-radius: .78rem .78rem .24rem .78rem;
        background: linear-gradient(145deg, rgba(109,40,217,.42), rgba(67,56,202,.3));
      }

      .bs-blackbox-chat-line.is-blackbox .bs-blackbox-chat-bubble {
        border-color: rgba(34,211,238,.17);
        background: linear-gradient(145deg, rgba(8,47,73,.55), rgba(15,23,42,.68));
      }

      .bs-blackbox-chat-bubble-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: .75rem;
        margin-bottom: .3rem;
      }

      .bs-blackbox-chat-bubble-head span {
        color: rgba(196,181,253,.9);
        font-size: .5rem;
        font-weight: 860;
        letter-spacing: .07em;
        text-transform: uppercase;
      }

      .bs-blackbox-chat-line.is-blackbox .bs-blackbox-chat-bubble-head span { color: #a5f3fc; }

      .bs-blackbox-chat-bubble-head time {
        color: rgba(148,163,184,.48);
        font-size: .46rem;
        white-space: nowrap;
      }

      .bs-blackbox-chat-bubble p {
        color: inherit;
        font-size: .68rem;
        line-height: 1.55;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
      }

      .bs-blackbox-chat-composer {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 2.45rem;
        align-items: end;
        gap: .45rem;
        border-top: 1px solid rgba(255,255,255,.065);
        padding: .65rem;
        background: rgba(2,4,10,.9);
      }

      .bs-blackbox-chat-input {
        min-height: 2.45rem;
        max-height: 7rem;
        resize: vertical;
        border: 1px solid rgba(255,255,255,.085);
        border-radius: .68rem;
        padding: .58rem .7rem;
        color: rgba(248,250,252,.95);
        background: rgba(15,23,42,.48);
        font-size: .65rem;
        line-height: 1.45;
        outline: none;
      }

      .bs-blackbox-chat-input:focus {
        border-color: rgba(167,139,250,.36);
        box-shadow: 0 0 0 3px rgba(139,92,246,.07);
      }

      .bs-blackbox-chat-send {
        display: grid;
        width: 2.45rem;
        height: 2.45rem;
        place-items: center;
        border: 1px solid rgba(167,139,250,.3);
        border-radius: .68rem;
        color: #f5f3ff;
        background: linear-gradient(145deg, rgba(124,58,237,.75), rgba(67,56,202,.7));
        box-shadow: 0 10px 24px rgba(76,29,149,.24);
      }

      .bs-blackbox-chat-send:disabled {
        cursor: not-allowed;
        opacity: .42;
      }

      .bs-blackbox-chat-closed {
        margin: 0;
        border-width: 1px 0 0 !important;
        border-radius: 0 !important;
        padding: .7rem !important;
        text-align: center;
      }

      .bs-blackbox-original-message {
        padding: .72rem .78rem;
        border: 1px solid rgba(103,232,249,.1);
        border-radius: .62rem;
        background: rgba(34,211,238,.025);
      }

      .bs-blackbox-original-message > span {
        display: block;
        margin-bottom: .32rem;
        color: rgba(126,213,225,.74);
        font-size: .55rem;
        font-weight: 850;
        letter-spacing: .13em;
        text-transform: uppercase;
      }

      .bs-blackbox-original-message p {
        color: rgba(229,233,244,.9);
        font-size: .7rem;
        line-height: 1.6;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }

      .bs-blackbox-thread {
        border-color: rgba(255,255,255,.065) !important;
        background: rgba(0,0,0,.24) !important;
        scrollbar-color: rgba(139,92,246,.24) transparent;
      }

      .bs-blackbox-thread-entry {
        border-color: rgba(255,255,255,.065) !important;
        background: rgba(255,255,255,.018) !important;
      }

      .bs-blackbox-thread-entry.is-presence {
        border-color: rgba(34,211,238,.14) !important;
        background: rgba(34,211,238,.04) !important;
      }

      .bs-blackbox-closed {
        border-color: rgba(255,255,255,.08) !important;
        color: rgba(161,161,170,.84) !important;
        background: rgba(255,255,255,.025) !important;
      }

      @media (max-width: 640px) {
        .bs-section-modal-backdrop {
          align-items: stretch !important;
          padding: 0 !important;
        }

        .bs-blackbox-modal {
          width: 100% !important;
          height: 100svh;
          max-width: none !important;
          max-height: 100svh;
          display: flex;
          flex-direction: column;
          border: 0 !important;
          border-radius: 0 !important;
          background:
            radial-gradient(circle at 92% 2%, rgba(111,71,206,.12), transparent 18rem),
            linear-gradient(180deg, #070914 0%, #03050b 52%, #020309 100%) !important;
        }

        .bs-blackbox-modal .bs-section-modal-header {
          min-height: 5.35rem;
          padding: calc(.78rem + env(safe-area-inset-top)) 1rem .78rem !important;
          background: rgba(4,6,13,.9);
          backdrop-filter: blur(18px);
        }

        .bs-blackbox-mark {
          width: 2.35rem;
          height: 2.35rem;
          border-radius: .65rem;
        }

        .bs-section-modal-heading {
          gap: .7rem;
        }

        .bs-section-modal-heading h2 {
          font-size: 1rem !important;
        }

        .bs-section-modal-heading p {
          max-width: 15.5rem;
          font-size: .62rem;
        }

        .bs-blackbox-modal .bs-section-modal-body {
          min-height: 0;
          max-height: none !important;
          flex: 1 1 auto;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          padding: .72rem .78rem calc(1rem + env(safe-area-inset-bottom)) !important;
          overscroll-behavior: contain;
          touch-action: pan-y;
          -webkit-overflow-scrolling: touch;
        }

        .bs-blackbox-workspace {
          grid-template-columns: minmax(0, 1fr) !important;
          gap: .85rem !important;
        }

        .bs-blackbox-composer,
        .bs-blackbox-receipt-panel {
          min-width: 0;
          border-radius: .82rem !important;
        }

        .bs-blackbox-composer {
          padding: .82rem;
        }

        .bs-blackbox-categories {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }

        .bs-blackbox-shared-settings-grid {
          grid-template-columns: minmax(0, 1fr);
          gap: .32rem;
        }

        .bs-blackbox-shared-settings {
          gap: .45rem;
          padding: .5rem;
        }

        .bs-blackbox-shared-settings > .bs-blackbox-field-group {
          gap: .32rem;
        }

        .bs-blackbox-shared-settings > .bs-blackbox-field-group .bs-blackbox-control {
          height: 2.25rem !important;
          min-height: 2.25rem;
        }

        .bs-blackbox-password-pairs {
          gap: .32rem;
        }

        .bs-blackbox-password-row {
          grid-template-columns: minmax(0, 1fr) minmax(8.4rem, 1.08fr);
          gap: .42rem;
          padding: .38rem .42rem;
        }

        .bs-blackbox-password-title {
          gap: .3rem;
          min-width: 0;
          font-size: .55rem;
        }

        .bs-blackbox-password-title span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-blackbox-password-input-shell input {
          height: 2.08rem !important;
          min-height: 2.08rem;
          padding-block: .3rem !important;
          font-size: .72rem;
          letter-spacing: .16em !important;
        }

        .bs-blackbox-password-input-shell button,
        html[class] .blackspace-public .bs-blackbox-password-input-shell button {
          right: .24rem;
          width: 1.62rem;
          height: 1.62rem;
        }

        .bs-blackbox-compact-setting {
          gap: .42rem;
          min-height: 2.2rem;
          padding-top: .32rem;
        }

        .bs-blackbox-selection-hint {
          font-size: .53rem;
        }

        .bs-blackbox-icon-options {
          gap: .2rem;
        }

        .bs-blackbox-icon-options button,
        html[class] .blackspace-public .bs-blackbox-icon-options button,
        .bs-blackbox-icon-options.is-access button {
          width: 1.82rem;
          height: 1.82rem;
          flex-basis: 1.82rem;
          border-radius: .5rem !important;
        }

        .bs-blackbox-icon-options button svg {
          width: .78rem;
          height: .78rem;
        }

        .bs-blackbox-category,
        html[class] .blackspace-public .bs-blackbox-category {
          height: 2.4rem !important;
          padding-inline: .4rem !important;
        }

        .bs-blackbox-message {
          height: 2.75rem;
          min-height: 2.75rem;
          overflow-y: hidden;
          transition: height 120ms ease, border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;
        }

        .bs-blackbox-submit-row {
          align-items: stretch !important;
          flex-direction: column;
          gap: .55rem !important;
        }

        .bs-blackbox-submit-row .bs-blackbox-submit {
          justify-content: center;
          width: 100%;
        }

        .bs-blackbox-submit-row .bs-blackbox-submit-label {
          text-align: center;
        }

        .bs-blackbox-receipt-panel {
          position: static;
          padding: .68rem !important;
        }

        .bs-blackbox-chat-header {
          align-items: center;
          flex-direction: row;
        }

        .bs-blackbox-chat-avatar {
          width: 2rem;
          height: 2rem;
          min-width: 2rem;
          min-height: 2rem;
          flex-basis: 2rem;
        }

        .bs-blackbox-chat-stream {
          max-height: clamp(9.5rem, 28dvh, 11.5rem);
          padding: .78rem .62rem;
          -webkit-overflow-scrolling: touch;
        }

        .bs-blackbox-chat-bubble {
          max-width: 89%;
        }

        .bs-blackbox-chat-bubble-head {
          align-items: flex-start;
          flex-direction: column;
          gap: .14rem;
        }

        .bs-blackbox-chat-input {
          font-size: .72rem;
        }
      }

      @keyframes bsLogoSignal {
        0%, 100% { color: rgba(103,232,249,0.46); box-shadow: inset 0 0 0 0.5px rgba(103,232,249,0.42), 0 0 8px rgba(34,211,238,0.12), 0 0 0 rgba(168,85,247,0); }
        25% { color: rgba(52,211,153,0.50); box-shadow: inset 0 0 0 0.5px rgba(52,211,153,0.48), 0 0 20px rgba(52,211,153,0.18), 0 0 34px rgba(34,211,238,0.10); }
        50% { color: rgba(217,70,239,0.52); box-shadow: inset 0 0 0 0.5px rgba(217,70,239,0.50), 0 0 22px rgba(217,70,239,0.18), 0 0 42px rgba(59,130,246,0.12); }
        75% { color: rgba(250,204,21,0.46); box-shadow: inset 0 0 0 0.5px rgba(250,204,21,0.42), 0 0 18px rgba(250,204,21,0.15), 0 0 36px rgba(244,63,94,0.10); }
      }

      @keyframes bsLogoHue {
        0% { filter: hue-rotate(0deg) saturate(1.05); }
        50% { filter: hue-rotate(180deg) saturate(1.24); }
        100% { filter: hue-rotate(360deg) saturate(1.05); }
      }

      @keyframes bsAwakeSignalPulse {
        0%, 100% { transform: scale(0.985); opacity: 0.82; }
        50% { transform: scale(1.025); opacity: 1; }
      }

      @keyframes bsRipplePulse {
        0%, 100% { transform: translate(-50%, -50%) scale(0.985); opacity: .45; }
        50% { transform: translate(-50%, -50%) scale(1.025); opacity: .58; }
      }

      @media (prefers-reduced-motion: reduce) {
        .blackspace-public::before,
        .blackspace-public::after,
        .bs-logo-signal,
        .bs-public-ripple,
        .bs-public-awake-signal {
          animation: none !important;
        }
      }

      @media (min-width: 641px) {
        .blackspace-public,
        html[class] .blackspace-public {
          min-height: 100dvh;
          overflow: hidden;
          background:
            radial-gradient(circle at 50% 30%, rgba(91, 64, 180, 0.16), transparent 25rem),
            radial-gradient(circle at 9% 55%, rgba(87, 57, 152, 0.14), transparent 24rem),
            radial-gradient(circle at 93% 54%, rgba(28, 46, 100, 0.18), transparent 25rem),
            linear-gradient(180deg, #070911 0%, #02040a 56%, #05060c 100%) !important;
          animation: none !important;
        }

        .blackspace-public::before {
          opacity: 0.16;
          background-image:
            linear-gradient(rgba(146, 126, 255, 0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(146, 126, 255, 0.025) 1px, transparent 1px);
          background-size: 86px 86px;
          animation: none !important;
          mask-image: linear-gradient(to bottom, rgba(0,0,0,.7), transparent 82%);
        }

        .blackspace-public::after {
          inset: auto -8% -1px;
          height: 46%;
          opacity: 0.82;
          mix-blend-mode: normal;
          background:
            linear-gradient(150deg, transparent 0 28%, rgba(6, 8, 16, 0.45) 28.5% 46%, transparent 46.5%),
            radial-gradient(ellipse at 15% 100%, rgba(28, 32, 52, 0.94), transparent 32%),
            radial-gradient(ellipse at 90% 100%, rgba(16, 19, 34, 0.98), transparent 34%),
            linear-gradient(180deg, transparent 0%, rgba(0,0,0,.64) 58%, rgba(0,0,0,.92) 100%);
          animation: none !important;
        }

        .bs-public-header {
          padding-top: 1.05rem !important;
          padding-inline: 1.5rem !important;
        }

        .bs-public-header > div {
          max-width: min(76rem, calc(100vw - 12rem)) !important;
        }

        .bs-public-logo-frame,
        html[class] .blackspace-public .bs-public-logo-frame {
          width: 2.35rem !important;
          height: 2.35rem !important;
          border-radius: 0.52rem !important;
          box-shadow:
            inset 0 0 0 1px rgba(103,232,249,.24),
            0 0 22px rgba(34,211,238,.08) !important;
        }

        .bs-public-brand-title {
          font-size: 1.02rem !important;
          font-weight: 900 !important;
          letter-spacing: -.02em;
        }

        .bs-public-brand-subtitle {
          display: block !important;
          margin-top: .35rem !important;
          color: rgba(166, 177, 214, .74) !important;
          font-size: .62rem !important;
          font-weight: 650 !important;
        }

        .bs-public-header .flex.items-center.gap-2 {
          gap: .55rem !important;
        }

        .bs-public-login-button,
        html[class] .blackspace-public .bs-public-login-button,
        .bs-public-signup-button,
        html[class] .blackspace-public .bs-public-signup-button {
          width: auto !important;
          min-width: 5.35rem;
          height: 2.75rem !important;
          border-radius: .48rem !important;
          gap: .48rem !important;
          padding-inline: .78rem !important;
          font-size: .66rem !important;
          font-weight: 850 !important;
          letter-spacing: .01em;
        }

        .bs-public-login-button,
        html[class] .blackspace-public .bs-public-login-button {
          border-color: rgba(94, 127, 184, .24) !important;
          color: rgba(241,245,249,.95) !important;
          background: rgba(5, 8, 16, .48) !important;
          box-shadow: inset 0 1px rgba(255,255,255,.035), 0 0 18px rgba(34,211,238,.035) !important;
        }

        .bs-public-signup-button,
        html[class] .blackspace-public .bs-public-signup-button {
          border: 1px solid rgba(147, 94, 255, .42) !important;
          color: rgba(248,250,252,.96) !important;
          background: linear-gradient(135deg, rgba(54, 29, 120, .7), rgba(22, 28, 83, .78)) !important;
          box-shadow: inset 0 1px rgba(255,255,255,.045), 0 0 28px rgba(117, 74, 255, .13) !important;
        }

        .bs-public-header-tooltip {
          position: static !important;
          width: auto !important;
          max-width: none !important;
          border: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          color: inherit !important;
          opacity: 1 !important;
          padding: 0 !important;
          transform: none !important;
        }

        .bs-public-hero-section {
          /* Keep the planet, signal, and cards anchored to this box. */
          --landing-planet-top: 7.5%;
          --landing-signal-top: 35%;
          --landing-copy-top: 48%;
          --landing-actions-bottom: 3rem;
          display: grid !important;
          min-height: 36rem;
          place-items: center !important;
          padding: 5.1rem 2rem 2rem !important;
        }

        .bs-public-hero-section::before {
          content: "";
          position: absolute;
          left: 50%;
          top: var(--landing-planet-top);
          width: min(66rem, 72vw);
          aspect-ratio: 1;
          border-radius: 999px;
          border-top: 1px solid rgba(133, 121, 226, .32);
          background:
            radial-gradient(ellipse at 28% 31%, rgba(93, 90, 132, .035) 0 2%, transparent 9%),
            radial-gradient(ellipse at 72% 24%, rgba(111, 104, 159, .025) 0 1%, transparent 8%),
            radial-gradient(ellipse at 38% 62%, rgba(80, 82, 112, .024) 0 3%, transparent 13%),
            radial-gradient(ellipse at 68% 57%, rgba(61, 66, 98, .026) 0 2%, transparent 12%),
            radial-gradient(circle at 50% 6%, rgba(75, 70, 128, .16) 0%, rgba(22, 23, 43, .12) 22%, transparent 48%),
            radial-gradient(circle at 50% 42%, #090b14 0%, #05070e 43%, #020309 72%, #010207 100%);
          box-shadow:
            inset 0 1px 0 rgba(155, 139, 255, .18),
            inset 0 24px 48px rgba(91, 76, 181, .055),
            inset 0 -100px 180px rgba(0, 0, 0, .72),
            0 -8px 48px rgba(99, 82, 207, .14),
            0 0 120px rgba(46, 38, 105, .07);
          transform: translateX(-50%);
          pointer-events: none;
        }

        .bs-public-hero-grid {
          display: grid !important;
          position: absolute !important;
          inset: 0;
          width: 100%;
          max-width: none !important;
          grid-template-columns: 1fr !important;
          gap: 0 !important;
          text-align: center;
        }

        .bs-public-copy {
          width: 100%;
          max-width: 42rem !important;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 0;
        }

        .bs-public-headline {
          position: absolute;
          top: var(--landing-copy-top);
          right: 0;
          left: 0;
          display: flex;
          width: calc(100% - 2rem);
          max-width: 42rem;
          flex-direction: column;
          align-items: center;
          margin-inline: auto;
          text-align: center;
        }

        .bs-public-status-badge {
          order: -1;
          display: grid !important;
          position: absolute;
          left: 50%;
          top: var(--landing-signal-top);
          z-index: 12;
          width: 6.45rem;
          height: 6.45rem;
          place-items: center;
          margin: 0 !important;
          border: 0 !important;
          background: transparent !important;
          padding: 0 !important;
          color: transparent !important;
          box-shadow: none !important;
          overflow: visible;
          transform: translate(-50%, -50%);
        }

        .bs-public-status-text {
          display: none !important;
        }

        .bs-public-awake-signal {
          position: relative;
          display: grid !important;
          width: 6.4rem;
          height: 6.4rem;
          place-items: center;
          color: #8b5cf6;
          animation: bsAwakeSignalPulse 3.2s ease-in-out infinite;
        }

        .bs-public-awake-signal svg {
          width: .72rem !important;
          height: .72rem !important;
          padding: 0;
          color: #8b5cf6;
          filter: drop-shadow(0 0 13px rgba(139,92,246,.9));
        }

        .bs-public-awake-signal::before,
        .bs-public-awake-signal::after {
          content: "";
          position: absolute;
          inset: 50%;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }

        .bs-public-awake-signal::before {
          width: 6.25rem;
          height: 6.25rem;
          background:
            repeating-radial-gradient(circle, rgba(139,92,246,.22) 0 1px, transparent 1px .82rem),
            radial-gradient(circle, rgba(139,92,246,.14), transparent 62%);
          mask-image: radial-gradient(circle, black 0 61%, transparent 72%);
        }

        .bs-public-awake-signal::after {
          width: 3.25rem;
          height: 3.25rem;
          border: 1px solid rgba(139,92,246,.18);
          box-shadow: 0 0 24px rgba(139,92,246,.11);
        }

        .bs-public-hero-title {
          width: 100%;
          max-width: 100% !important;
          font-size: clamp(2.05rem, 3.2vw, 3.05rem) !important;
          font-weight: 830 !important;
          line-height: 1.08 !important;
          letter-spacing: -.045em;
          text-shadow: 0 0 24px rgba(255,255,255,.055);
        }

        .bs-public-hero-title span {
          color: #9b6cff;
          text-shadow: 0 0 32px rgba(139,92,246,.24);
        }

        .bs-public-hero-copy {
          width: 100%;
          margin: 1rem auto 0 !important;
          max-width: 26rem !important;
          text-align: center;
          color: rgba(190, 196, 231, .84) !important;
          font-size: .82rem !important;
          font-weight: 560 !important;
          line-height: 1.55 !important;
        }

        .bs-public-actions {
          display: grid !important;
          position: absolute;
          left: 50%;
          bottom: var(--landing-actions-bottom);
          z-index: 20;
          width: min(31rem, 100%);
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1.55rem !important;
          margin-top: 0 !important;
          transform: translateX(-50%);
        }

        .bs-public-action-card,
        html[class] .blackspace-public .bs-public-action-card {
          display: grid !important;
          height: 4.35rem !important;
          grid-template-columns: 2.65rem minmax(0, 1fr) auto;
          gap: .92rem !important;
          align-items: center;
          border-radius: .52rem !important;
          padding: .82rem .92rem !important;
          text-align: left;
          background:
            radial-gradient(circle at 0% 0%, rgba(34, 211, 238, .08), transparent 44%),
            rgba(5, 9, 17, .58) !important;
          border-color: rgba(56, 189, 248, .42) !important;
          box-shadow: inset 0 1px rgba(255,255,255,.035), 0 20px 42px rgba(0,0,0,.22) !important;
        }

        .bs-public-blackbox-card,
        html[class] .blackspace-public .bs-public-blackbox-card {
          border-color: rgba(139,92,246,.46) !important;
          background:
            radial-gradient(circle at 0% 0%, rgba(139,92,246,.12), transparent 44%),
            rgba(9, 6, 19, .58) !important;
        }

        .bs-public-action-icon {
          display: grid !important;
          width: 2.45rem;
          height: 2.45rem;
          place-items: center;
          border-radius: .55rem;
          background: rgba(20, 44, 77, .36);
          color: #60a5fa;
        }

        .bs-public-blackbox-card .bs-public-action-icon {
          background: rgba(83, 48, 143, .34);
          color: #a78bfa;
        }

        .bs-public-action-icon svg {
          width: 1.05rem;
          height: 1.05rem;
        }

        .bs-public-action-text {
          display: grid !important;
          min-width: 0;
          gap: .23rem;
        }

        .bs-public-action-text strong {
          color: rgba(248,250,252,.96);
          font-size: .86rem;
          font-weight: 900;
          line-height: 1;
        }

        .bs-public-action-text small {
          display: block !important;
          color: rgba(177, 185, 216, .76);
          font-size: .75rem;
          font-weight: 650;
          line-height: 1.2;
        }

        .bs-public-action-arrow {
          display: block !important;
          color: rgba(181, 188, 226, .82);
        }

        .bs-public-horizon {
          display: block;
          position: absolute;
          inset: 0;
          opacity: .76;
          background:
            radial-gradient(ellipse at 50% 13%, rgba(83, 68, 171, .13), transparent 34%),
            radial-gradient(ellipse at 50% 52%, transparent 0%, rgba(2, 3, 8, .34) 48%, rgba(1, 2, 6, .82) 72%, transparent 82%);
        }

        .bs-public-ripple {
          display: block;
          position: absolute;
          left: 50%;
          top: 50%;
          width: 6.4rem;
          height: 6.4rem;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          background:
            repeating-radial-gradient(circle, rgba(139,92,246,.19) 0 1px, transparent 1px .86rem),
            radial-gradient(circle, rgba(139,92,246,.18), transparent 52%);
          opacity: .52;
          mask-image: radial-gradient(circle, transparent 0 11%, black 12% 64%, transparent 76%);
          animation: bsRipplePulse 3.2s ease-in-out infinite;
        }

        .bs-public-ripple span,
        .bs-public-ripple i,
        .bs-public-ripple b {
          position: absolute;
          left: 50%;
          top: 50%;
          border-radius: 999px;
          transform: translate(-50%, -50%);
        }

        .bs-public-ripple span {
          width: .48rem;
          height: .48rem;
          background: #8b5cf6;
          box-shadow: 0 0 18px rgba(139,92,246,.92);
        }

        .bs-public-ripple i {
          width: 2.2rem;
          height: 2.2rem;
          border: 1px solid rgba(139,92,246,.18);
        }

        .bs-public-ripple b {
          width: 4.4rem;
          height: 4.4rem;
          border: 1px solid rgba(139,92,246,.11);
        }

        .bs-public-side-note {
          position: absolute;
          z-index: 12;
          display: block;
          pointer-events: none;
          color: rgba(144, 153, 196, .82);
          top: 21%;
          left: 5.8%;
          padding-left: 1rem;
          border-left: 1px solid rgba(121, 103, 214, .32);
          font-size: .75rem;
          font-weight: 650;
          letter-spacing: .22em;
          line-height: 1.35;
          text-transform: uppercase;
        }

      }

      @media (max-width: 640px) {
        .blackspace-public,
        html[class] .blackspace-public {
          min-height: 100svh;
          background:
            radial-gradient(circle at 50% 37%, rgba(128, 78, 255, 0.20), transparent 8rem),
            radial-gradient(circle at 10% 82%, rgba(168, 85, 247, 0.16), transparent 11rem),
            radial-gradient(circle at 96% 18%, rgba(37, 99, 235, 0.13), transparent 10rem),
            linear-gradient(180deg, #050614 0%, #03040c 54%, #05060f 100%) !important;
          animation: none !important;
        }

        .blackspace-public::before {
          opacity: 0.58;
          background:
            radial-gradient(circle at 16% 29%, rgba(139, 92, 246, 0.18) 0 1px, transparent 2px),
            radial-gradient(circle at 86% 18%, rgba(59, 130, 246, 0.14) 0 1px, transparent 2px),
            radial-gradient(circle at 32% 77%, rgba(217, 70, 239, 0.12) 0 1px, transparent 2px),
            linear-gradient(180deg, rgba(255,255,255,0.018), transparent 40%);
          background-size: auto;
          animation: none !important;
        }

        .blackspace-public::after {
          inset: 24% -20% auto;
          height: 19rem;
          opacity: 0.42;
          background:
            radial-gradient(circle, rgba(139, 92, 246, 0.18), transparent 38%),
            radial-gradient(circle, rgba(34, 211, 238, 0.08), transparent 58%);
          animation: none !important;
        }

        .bs-public-header {
          padding: calc(1.65rem + env(safe-area-inset-top)) 1.25rem 0 !important;
        }

        .bs-public-header > div {
          max-width: 100% !important;
        }

        .bs-public-logo-frame,
        html[class] .blackspace-public .bs-public-logo-frame {
          width: 2rem !important;
          height: 2rem !important;
          border-radius: 0.46rem !important;
          box-shadow:
            inset 0 0 0 0.5px rgba(168, 85, 247, 0.78),
            0 0 18px rgba(168, 85, 247, 0.20) !important;
        }

        .bs-public-brand-title {
          font-size: 0.95rem !important;
          letter-spacing: 0 !important;
        }

        .bs-public-brand-subtitle {
          display: none !important;
        }

        .bs-public-login-button,
        html[class] .blackspace-public .bs-public-login-button,
        .bs-public-signup-button,
        html[class] .blackspace-public .bs-public-signup-button {
          width: 2.75rem !important;
          height: 2.75rem !important;
          flex-shrink: 0;
          border-radius: 0.42rem !important;
          padding: 0 !important;
          font-size: 0.64rem !important;
          line-height: 1 !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 18px rgba(92, 124, 255, 0.10) !important;
        }

        .bs-public-signup-button,
        html[class] .blackspace-public .bs-public-signup-button {
          min-width: 2.22rem;
          color: #f8fafc !important;
          background:
            linear-gradient(135deg, rgba(126, 34, 206, 0.92), rgba(37, 99, 235, 0.92)) !important;
        }

        .bs-public-hero-section {
          display: flex !important;
          min-height: 29rem;
          align-items: stretch !important;
          padding: calc(5.35rem + env(safe-area-inset-top)) 1.25rem 1.25rem !important;
        }

        .bs-public-hero-grid {
          display: flex !important;
          max-width: 100% !important;
          height: 100%;
          flex-direction: column;
          justify-content: center;
          gap: 0 !important;
        }

        .bs-public-copy {
          display: flex;
          width: 100%;
          min-height: min(33.5rem, calc(100svh - 12rem));
          max-width: none !important;
          flex: 1 1 auto;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          padding-top: clamp(7.1rem, 18svh, 8.7rem);
          text-align: center;
        }

        .bs-public-headline {
          display: flex;
          width: 100%;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .bs-public-status-badge {
          display: inline-flex !important;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2.8rem;
          margin: 0 auto !important;
          border: 0 !important;
          background: transparent !important;
          padding: 0 !important;
          color: rgba(248, 250, 252, 0.92) !important;
          font-size: 1.08rem !important;
          font-weight: 850 !important;
          line-height: 1.2 !important;
          box-shadow: none !important;
        }

        .bs-public-status-badge svg {
          position: relative;
          z-index: 2;
          display: block;
          width: 4.7rem !important;
          height: 4.7rem !important;
          padding: 1.45rem;
          border: 1px solid rgba(139, 92, 246, 0.22);
          border-radius: 999px;
          background:
            radial-gradient(circle, rgba(139, 92, 246, 0.22), rgba(139, 92, 246, 0.06) 48%, transparent 70%);
          color: rgb(139, 92, 246);
          filter: drop-shadow(0 0 18px rgba(139, 92, 246, 0.24));
        }

        .bs-public-awake-signal {
          position: relative;
          width: 4.7rem;
          height: 4.7rem;
          flex: 0 0 4.7rem;
          isolation: isolate;
          animation: bsAwakeSignalPulse 3.2s ease-in-out infinite;
        }

        .bs-public-awake-signal::before,
        .bs-public-awake-signal::after {
          content: "";
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 0;
          border-radius: 999px;
          pointer-events: none;
          transform: translate(-50%, -50%);
        }

        .bs-public-awake-signal::before {
          width: 8.8rem;
          height: 8.8rem;
          background:
            repeating-radial-gradient(circle, rgba(139, 92, 246, 0.14) 0 1px, transparent 1px 1.05rem),
            radial-gradient(circle, rgba(168, 85, 247, 0.16), transparent 58%);
          mask-image: radial-gradient(circle, black 0 66%, transparent 76%);
        }

        .bs-public-awake-signal::after {
          width: 5.95rem;
          height: 5.95rem;
          border: 1px solid rgba(139, 92, 246, 0.12);
          box-shadow: 0 0 30px rgba(139, 92, 246, 0.10);
        }

        .bs-public-hero-title {
          display: none !important;
        }

        .bs-public-hero-copy {
          width: min(16.25rem, 100%);
          margin: 1.1rem auto 0 !important;
          max-width: 16.25rem !important;
          text-align: center;
          color: rgba(198, 199, 214, 0.82) !important;
          font-size: 0.76rem !important;
          font-weight: 550 !important;
          line-height: 1.62 !important;
        }

        .bs-public-copy-desktop {
          display: none !important;
        }

        .bs-public-copy-mobile {
          display: inline !important;
        }

        .bs-public-actions {
          display: grid !important;
          width: 100%;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 0.74rem !important;
          margin-top: auto !important;
          padding-top: 4.6rem;
        }

        .bs-public-action-card,
        html[class] .blackspace-public .bs-public-action-card {
          display: grid !important;
          height: 4.55rem !important;
          min-width: 0 !important;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 0.62rem !important;
          border-radius: 0.58rem !important;
          border-color: rgba(139, 92, 246, 0.34) !important;
          background:
            linear-gradient(135deg, rgba(139, 92, 246, 0.11), rgba(34, 211, 238, 0.045)),
            rgba(8, 10, 20, 0.68) !important;
          padding: 0.78rem 0.7rem !important;
          text-align: left;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), 0 16px 38px rgba(0, 0, 0, 0.24);
        }

        .bs-public-blackbox-card,
        html[class] .blackspace-public .bs-public-blackbox-card {
          border-color: rgba(255, 255, 255, 0.08) !important;
          background:
            linear-gradient(135deg, rgba(255, 255, 255, 0.035), rgba(139, 92, 246, 0.045)),
            rgba(7, 9, 18, 0.58) !important;
        }

        .bs-public-action-icon {
          display: grid !important;
          width: 2.05rem;
          height: 2.05rem;
          place-items: center;
          border-radius: 0.48rem;
          background: rgba(139, 92, 246, 0.12);
          color: rgb(167, 139, 250);
        }

        .bs-public-blackbox-card .bs-public-action-icon {
          background: rgba(255, 255, 255, 0.045);
          color: rgb(196, 181, 253);
        }

        .bs-public-action-text {
          display: grid !important;
          min-width: 0;
          gap: 0.18rem;
        }

        .bs-public-action-text strong {
          overflow: hidden;
          color: rgba(248, 250, 252, 0.94);
          font-size: 0.78rem;
          font-weight: 850;
          line-height: 1.1;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-public-action-text small {
          display: block !important;
          overflow: hidden;
          color: rgba(165, 166, 183, 0.80);
          font-size: 0.68rem;
          font-weight: 650;
          line-height: 1.22;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bs-public-action-arrow {
          display: block !important;
          color: rgba(198, 199, 214, 0.72);
        }

      }

      @media (max-width: 380px) {
        .bs-public-action-card,
        html[class] .blackspace-public .bs-public-action-card {
          grid-template-columns: 2.05rem minmax(0, 1fr) !important;
          gap: .45rem !important;
          padding-inline: .55rem !important;
        }

        .bs-public-action-text small,
        .bs-public-action-arrow {
          display: none !important;
        }
      }

      @media (max-width: 640px) and (max-height: 690px) {
        .bs-public-hero-section {
          padding-top: calc(4.6rem + env(safe-area-inset-top)) !important;
          padding-bottom: .75rem !important;
        }

        .bs-public-copy {
          min-height: min(29.2rem, calc(100svh - 9.7rem));
          padding-top: 5.25rem;
        }

        .bs-public-status-badge {
          gap: 2.3rem;
        }

        .bs-public-status-badge svg {
          width: 4.08rem !important;
          height: 4.08rem !important;
          padding: 1.22rem;
        }

        .bs-public-awake-signal {
          width: 4.08rem;
          height: 4.08rem;
          flex-basis: 4.08rem;
        }

        .bs-public-awake-signal::before {
          width: 7.65rem;
          height: 7.65rem;
        }

        .bs-public-awake-signal::after {
          width: 5.15rem;
          height: 5.15rem;
        }

        .bs-public-actions {
          padding-top: 3.2rem;
        }

        .bs-public-action-card,
        html[class] .blackspace-public .bs-public-action-card {
          height: 4.1rem !important;
          padding-block: 0.62rem !important;
        }
      }
    `}</style>
  );
}
